/**
 * Controlador principal de la aplicación FinVoz (spec/02-architecture.md).
 *
 * Conecta:
 *  - Almacenamiento local durable (IndexedDbLocalStore)
 *  - Outbox y motor de sincronización idempotente con Sheets (ADR OQ-1)
 *  - Cadenas de integridad por dispositivo (INV-9, FR-STORE-12)
 *  - Google OAuth PKCE con token en memoria (INV-8, ADR OQ-2)
 *  - Captura por voz y manual simétrica (FR-VOICE-1..12)
 *  - Vistas y navegación reactiva pura sin framework
 */

import type { ActiveTab, AppState, Budget, Goal, ThemeMode, VoiceState, BackupImportPreview } from "./types.js";
import {
  createFinVozLocalStore,
  createProfileDataStore,
  createProfileRegistryStore,
  seedInitialAccountsIfEmpty,
} from "../storage/indexeddb-factory.js";
import { Outbox, type OutboxItem } from "../storage/outbox.js";
import type { LocalStore } from "../storage/local-store.js";
import { createInMemoryTokenStore, type TokenStore } from "../auth/token-store.js";
import {
  createGoogleOAuthConfig,
  handleGoogleOAuthCallback,
  startGoogleOAuth,
  logoutAndPurgeLocalData,
  DEFAULT_GOOGLE_CLIENT_ID,
  getGoogleOAuthRedirectUri,
} from "../auth/google-auth.js";
import { GoogleSheetsAdapter, GoogleSheetsSummaryWriter, createFinVozSpreadsheet } from "../sync/google-sheets-adapter.js";
import { exportMovimientos, exportResumen } from "../sync/sheets-export.js";
import { RealQuoteProvider, getYahooTicker } from "../market/real-quote-provider.js";
import { QuoteCache } from "../market/quote-cache.js";
import type { PriceQuote } from "../market/quote-provider.js";
import { INVESTMENT_CATALOG } from "../market/catalog.js";
import { archiveAccount } from "../core/account.js";
import { accountBalance, instrumentQuantity } from "../core/ledger.js";
import { buildEntry, reverseEntry, type Entry } from "../core/entry.js";
import { appendChainedEntry, type ChainedEntry, type DeviceChainState, initDeviceChain } from "../integrity/chain.js";
import { generateEntryId } from "../core/ids.js";
import {
  createProfile,
  deleteProfile,
  ensureActiveProfile,
  listProfiles,
  profileDataDbName,
  selectProfile,
  type Profile,
} from "../profile/profile.js";
import {
  exportBackup,
  importBackup,
  parseBackupFile,
  serializeBackupFile,
  type FinvozBackupFile,
  type ImportMode,
} from "../backup/backup.js";
import { BrowserSpeechRecognizer, isSpeechRecognitionSupported, type SpeechRecognitionCallbacks } from "../voice/speech-recognition.js";
import { parseFinancialExpression, type ParsedProposal } from "../voice/deterministic-parser.js";
import { simulateCompoundInterest, amortizationSchedule, periodsToSavingsGoal } from "../simulators/simulators.js";
import { formatMinorUnits } from "./format.js";
import { escapeHtml, escapeAttr } from "./sanitize.js";

import { renderDashboard } from "./views/dashboard.js";
import { renderCapture } from "./views/capture.js";
import { renderLedger } from "./views/ledger.js";
import { renderAccounts } from "./views/accounts.js";
import { renderBudgets } from "./views/budgets.js";
import { renderInvestments, formatVolume } from "./views/investments.js";
import { renderSimulators } from "./views/simulators.js";
import { renderSettings } from "./views/settings.js";
import {
  loadMarketSegment,
  searchMarketInstruments,
  type MarketSegment,
  type MarketInstrumentRecord,
} from "../market/global-catalog.js";

export class FinVozApp {
  private state: AppState;
  private tokenStore: TokenStore;
  private localStore!: LocalStore;
  private outbox!: Outbox;
  private quoteProvider: RealQuoteProvider;
  private quoteCache!: QuoteCache;
  private profileRegistry: any;
  private deviceChainState: DeviceChainState;
  private speechRecognizer: BrowserSpeechRecognizer | null = null;
  private activeProposal: ParsedProposal | null = null;
  private quotes: PriceQuote[] = [];
  private currentMarketRecords: MarketInstrumentRecord[] = [];
  private deferredPrompt: any = null;
  private voiceAutoTimeoutTimer: any = null;
  private voiceHasReceivedTranscript = false;

  constructor() {
    this.tokenStore = createInMemoryTokenStore();
    this.quoteProvider = new RealQuoteProvider();

    const savedTheme = (typeof localStorage !== "undefined" ? localStorage.getItem("finvoz_theme") : null) as ThemeMode || "system";
    const savedCurrency = ((typeof localStorage !== "undefined" ? localStorage.getItem("finvoz_currency") : null) as ("PEN" | "USD" | "EUR")) || "PEN";

    // Estado inicial por defecto: Experiencia Voice-First (primera pantalla es Captura)
    this.state = {
      activeTab: "capture",
      isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
      theme: savedTheme,
      currency: savedCurrency,
      isNavOpen: false,
      voiceState: "idle",
      voiceErrorMessage: null,
      entries: [],
      accounts: [],
      budgets: [],
      goals: [],
      outboxItems: [],
      selectedPeriod: "month",
      googleClientId:
        (typeof localStorage !== "undefined" ? localStorage.getItem("finvoz_google_client_id") : null) ||
        ((typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID)) ||
        DEFAULT_GOOGLE_CLIENT_ID,
      spreadsheetId: (typeof localStorage !== "undefined" ? localStorage.getItem("finvoz_spreadsheet_id") : null) || null,
      googleUserEmail: null,
      quarantinedEntryIds: [],
      notification: null,
      profiles: [],
      activeProfileId: null,
      pendingImportPreview: null,
      importBackupModalOpen: false,
    };

    const storedDeviceId = (typeof localStorage !== "undefined" ? localStorage.getItem("finvoz_device_id") : null) || `dev-${generateEntryId().slice(0, 8)}`;
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("finvoz_device_id", storedDeviceId);
    }
    this.deviceChainState = { deviceId: storedDeviceId, lastSeq: 0, lastChainHash: "" };
  }

  async init(): Promise<void> {
    // 1. Perfiles locales (IndexedDB es la autoridad; revisión de ADR OQ-1, 2026-09-10):
    //    abrir el registro de perfiles, garantizar que exista un perfil activo (primer
    //    arranque: "Perfil principal"), y abrir el almacén de datos aislado de ESE perfil.
    this.profileRegistry = await createProfileRegistryStore();
    const activeProfile = await ensureActiveProfile(this.profileRegistry);
    this.state.profiles = await listProfiles(this.profileRegistry);
    this.state.activeProfileId = activeProfile.profileId;

    this.localStore = await createProfileDataStore(activeProfile.profileId);
    this.outbox = new Outbox(this.localStore);
    this.quoteCache = new QuoteCache(this.localStore, this.quoteProvider);

    // 2. Sembrar cuentas por defecto si esta vacio (FR-ACCT-6)
    await seedInitialAccountsIfEmpty(this.localStore);

    // 3. Cargar datos desde IndexedDB
    await this.loadLocalData();

    // 4. Restaurar estado de cadena del dispositivo si existe o inicializar
    const savedChainState = await this.localStore.readMeta("device_chain_state");
    if (savedChainState) {
      this.deviceChainState = savedChainState as DeviceChainState;
    } else {
      this.deviceChainState = await initDeviceChain("finvoz-ledger-v1", this.deviceChainState.deviceId);
      await this.localStore.writeMeta("device_chain_state", this.deviceChainState);
    }

    // 5. Aplicar tema inicial y escuchar cambios de preferencia del sistema (FR-UX)
    this.applyTheme();
    if (typeof window !== "undefined" && window.matchMedia) {
      window.matchMedia("(prefers-color-scheme: light)").addEventListener("change", () => {
        if (this.state.theme === "system") {
          this.applyTheme();
        }
      });
    }

    // 6. Escuchar eventos de red (offline/online)
    window.addEventListener("online", () => {
      this.state.isOnline = true;
      this.showToast("Conexión restablecida", "info");
      this.render();
      // Sin sincronización automática (revisión de ADR OQ-1, 2026-09-10): Sheets es
      // exportación opcional, nunca autoritativa. La exportación es siempre manual
      // (botón "Exportar Movimientos" en Ajustes).
    });

    window.addEventListener("offline", () => {
      this.state.isOnline = false;
      this.showToast("Modo sin conexión activado", "warning");
      this.render();
    });

    // 7. Capturar evento de instalación PWA
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      const installBtn = document.getElementById("pwa-install-btn");
      if (installBtn) installBtn.style.display = "block";
    });

    // 8. Procesar callback de OAuth PKCE si está presente en la URL
    if (window.location.search.includes("code=") && window.location.search.includes("state=")) {
      const config = createGoogleOAuthConfig(
        this.state.googleClientId || DEFAULT_GOOGLE_CLIENT_ID,
        getGoogleOAuthRedirectUri(),
      );
      const res = await handleGoogleOAuthCallback(config, this.tokenStore);
      if (res.success) {
        this.state.googleUserEmail = "Usuario Conectado (Google)";
        this.showToast("Autenticado con Google con éxito", "success");
      } else if (res.error) {
        this.showToast(res.error, "error");
      }
    }

    // 9. Exponer bindings globales para la UI interactiva
    this.bindGlobalHandlers();

    // 10. Delegar eventos DOM globales para cumplimiento estricto de CSP sin 'unsafe-inline'
    this.setupGlobalEventListeners();

    // 11. Renderizar la interfaz inicial
    this.render();

    // 12. En pantalla inicial intentar SpeechRecognition automáticamente (Requirement 3)
    if (this.state.activeTab === "capture") {
      this.attemptAutoStartVoice();
    }
  }

  private async loadLocalData(): Promise<void> {
    const rawAccounts = await this.localStore.list("accounts");
    this.state.accounts = rawAccounts.map((i: any) => i.value);

    const rawEntries = await this.localStore.list("entries");
    this.state.entries = rawEntries.map((i: any) => i.value);

    // Saneamiento de posición fantasma: desvincular GLOBAL-EQ huérfano de la base de datos local
    const sanitizedAccounts = [];
    for (const acc of this.state.accounts) {
      if (acc.instrumentId === "GLOBAL-EQ") {
        const hasEntries = this.state.entries.some((e) =>
          e.entry.postings.some((p) => p.accountId === acc.accountId),
        );
        if (!hasEntries) {
          const updated = { ...acc, instrumentId: null };
          await this.localStore.putBatch("accounts", [{ id: acc.accountId, value: updated }]);
          sanitizedAccounts.push(updated);
          continue;
        }
      }
      sanitizedAccounts.push(acc);
    }
    this.state.accounts = sanitizedAccounts;

    const rawBudgetsGoals = await this.localStore.list("budgets_goals");
    this.state.budgets = rawBudgetsGoals.filter((i: any) => i.value.period).map((i: any) => i.value);
    this.state.goals = rawBudgetsGoals.filter((i: any) => i.value.targetDate).map((i: any) => i.value);

    this.state.outboxItems = await this.outbox.listPending();
  }

  showToast(message: string, type: "success" | "info" | "warning" | "error" = "info"): void {
    this.state.notification = { message, type };
    this.renderToast();
    setTimeout(() => {
      if (this.state.notification?.message === message) {
        this.state.notification = null;
        this.renderToast();
      }
    }, 4000);
  }

  private renderToast(): void {
    let container = document.getElementById("toast-container");
    if (!container) {
      container = document.createElement("div");
      container.id = "toast-container";
      container.className = "toast-container";
      document.body.appendChild(container);
    }
    container.textContent = "";
    if (this.state.notification) {
      const toastEl = document.createElement("div");
      toastEl.className = `toast toast-${this.state.notification.type}`;
      toastEl.setAttribute("role", "alert");
      toastEl.textContent = this.state.notification.message;
      container.appendChild(toastEl);
    }
  }

  applyTheme(): void {
    if (typeof document === "undefined") return;
    const theme = this.state.theme;
    if (theme === "system") {
      document.documentElement.removeAttribute("data-theme");
    } else {
      document.documentElement.setAttribute("data-theme", theme);
    }
  }

  setTheme(theme: ThemeMode): void {
    this.state.theme = theme;
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("finvoz_theme", theme);
    }
    this.applyTheme();
    this.render();
  }

  toggleTheme(): void {
    const nextTheme: ThemeMode =
      this.state.theme === "system" ? "light" : this.state.theme === "light" ? "dark" : "system";
    this.setTheme(nextTheme);
  }

  private getThemeBadgeLabel(): string {
    if (this.state.theme === "light") return "☀️ Claro";
    if (this.state.theme === "dark") return "🌙 Oscuro";
    return "🌓 Auto";
  }

  render(): void {
    const appEl = document.getElementById("app");
    if (!appEl) return;

    appEl.innerHTML = `
      <!-- Header con Hamburguesa, Marca, Google PKCE, Selector de Moneda y Estado -->
      <header class="header" role="banner">
        <div class="header-left">
          <button
            class="hamburger-btn"
            id="hamburger-btn"
            data-testid="hamburger-btn"
            data-action="toggle-nav"
            aria-label="Abrir menú de navegación"
            aria-expanded="${this.state.isNavOpen ? 'true' : 'false'}"
            aria-controls="nav-drawer"
            title="Menú de navegación"
          >
            <span class="hamburger-line"></span>
            <span class="hamburger-line"></span>
            <span class="hamburger-line"></span>
          </button>
          <div class="brand">
            <h1>FinVoz</h1>
            <span class="brand-badge">PWA v1</span>
          </div>
        </div>

        <div class="header-status">
          <!-- Iniciar sesión con Google OAuth PKCE en Header (Requirement 4) -->
          ${!this.state.googleUserEmail
            ? `<button class="header-google-btn" data-action="connect-google" data-testid="header-connect-google-btn" title="Iniciar sesión con Google (OAuth PKCE)">
                 <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style="display: inline-block; vertical-align: -2px;"><path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.344-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z"/></svg>
                 <span>Iniciar sesión con Google</span>
               </button>`
            : `<span class="header-google-user" data-testid="header-google-user" title="Sesión activa: ${escapeAttr(this.state.googleUserEmail)}">
                 👤 Conectado
               </span>
               ${!this.state.spreadsheetId
                 ? `<button class="header-sheet-btn" data-action="create-sheet" data-testid="header-create-sheet-btn" title="Crear Hoja de Cálculo en Google Drive">
                      📊 Crear Hoja en Sheets
                    </button>`
                 : `<a href="https://docs.google.com/spreadsheets/d/${encodeURIComponent(this.state.spreadsheetId)}" target="_blank" rel="noopener" class="header-sheet-link status-pill" title="Abrir Hoja de Cálculo vinculada">
                      📊 Hoja Sheets &nearr;
                    </a>`
               }`
          }

          <!-- Selector de Moneda PEN / USD / EUR (Requirement 2) -->
          <div class="currency-selector-wrapper">
            <label for="currency-select" class="sr-only">Moneda activa</label>
            <select
              id="currency-select"
              class="currency-select"
              data-testid="currency-select"
              data-action="change-currency"
              aria-label="Seleccionar moneda"
            >
              <option value="PEN" ${this.state.currency === "PEN" ? "selected" : ""}>PEN (S/)</option>
              <option value="USD" ${this.state.currency === "USD" ? "selected" : ""}>USD ($)</option>
              <option value="EUR" ${this.state.currency === "EUR" ? "selected" : ""}>EUR (€)</option>
            </select>
          </div>

          <button
            class="status-pill"
            id="header-theme-btn"
            data-testid="toggle-theme-btn"
            data-action="toggle-theme"
            title="Cambiar tema visual (Claro / Oscuro / Sistema)"
            aria-label="Cambiar tema visual"
            style="cursor: pointer; border: 1px solid var(--border);"
          >
            ${this.getThemeBadgeLabel()}
          </button>
          <span class="status-pill ${this.state.isOnline ? 'status-online' : 'status-offline'}">
            ${this.state.isOnline ? '● En línea' : '○ Sin conexión'}
          </span>
        </div>
      </header>

      <!-- Backdrop para Drawer Móvil (WCAG 2.1 AA) -->
      <div
        id="nav-drawer-backdrop"
        class="nav-drawer-backdrop ${this.state.isNavOpen ? 'open' : ''}"
        data-action="close-nav"
        aria-hidden="true"
      ></div>

      <!-- Layout con Drawer Lateral Adaptado (WCAG 2.1 AA) -->
      <div class="app-body">
        <nav
          id="nav-drawer"
          data-testid="nav-drawer"
          class="nav-drawer ${this.state.isNavOpen ? 'open' : ''}"
          role="tablist"
          aria-label="Secciones principales"
        >
          <div class="drawer-header">
            <span class="drawer-title">Menú FinVoz</span>
            <button
              class="drawer-close-btn"
              data-action="close-nav"
              data-testid="drawer-close-btn"
              aria-label="Cerrar menú de navegación"
            >✕</button>
          </div>
          <div class="drawer-nav-items">
            <button class="nav-tab ${this.state.activeTab === 'capture' ? 'active' : ''}" role="tab" aria-selected="${this.state.activeTab === 'capture'}" data-action="navigate" data-tab="capture">🎤 Captura</button>
            <button class="nav-tab ${this.state.activeTab === 'dashboard' ? 'active' : ''}" role="tab" aria-selected="${this.state.activeTab === 'dashboard'}" data-action="navigate" data-tab="dashboard">📊 Dashboard</button>
            <button class="nav-tab ${this.state.activeTab === 'ledger' ? 'active' : ''}" role="tab" aria-selected="${this.state.activeTab === 'ledger'}" data-action="navigate" data-tab="ledger">📖 Libro Diario</button>
            <button class="nav-tab ${this.state.activeTab === 'accounts' ? 'active' : ''}" role="tab" aria-selected="${this.state.activeTab === 'accounts'}" data-action="navigate" data-tab="accounts">🏦 Cuentas</button>
            <button class="nav-tab ${this.state.activeTab === 'budgets' ? 'active' : ''}" role="tab" aria-selected="${this.state.activeTab === 'budgets'}" data-action="navigate" data-tab="budgets">🎯 Presupuestos & Metas</button>
            <button class="nav-tab ${this.state.activeTab === 'investments' ? 'active' : ''}" role="tab" aria-selected="${this.state.activeTab === 'investments'}" data-action="navigate" data-tab="investments">📈 Inversiones</button>
            <button class="nav-tab ${this.state.activeTab === 'simulators' ? 'active' : ''}" role="tab" aria-selected="${this.state.activeTab === 'simulators'}" data-action="navigate" data-tab="simulators">🧮 Simuladores</button>
            <button class="nav-tab ${this.state.activeTab === 'settings' ? 'active' : ''}" role="tab" aria-selected="${this.state.activeTab === 'settings'}" data-action="navigate" data-tab="settings">⚙️ Ajustes & Sync</button>
          </div>
        </nav>

        <!-- Contenido Principal -->
        <main class="main-content" role="main">
          ${this.renderActiveView()}
        </main>
      </div>
    `;

    this.attachViewSpecificListeners();
  }

  private renderActiveView(): string {
    switch (this.state.activeTab) {
      case "dashboard":
        return renderDashboard(this.state, (tab) => this.navigateTo(tab));
      case "capture":
        return renderCapture(this.state);
      case "ledger":
        return renderLedger(this.state);
      case "accounts":
        return renderAccounts(this.state);
      case "budgets":
        return renderBudgets(this.state);
      case "investments":
        return renderInvestments(this.state, this.quotes, this.currentMarketRecords);
      case "simulators":
        return renderSimulators(this.state);
      case "settings":
        return renderSettings(this.state);
      default:
        return renderDashboard(this.state, (tab) => this.navigateTo(tab));
    }
  }

  private attachViewSpecificListeners(): void {
    if (this.state.activeTab === "dashboard") {
      const select = document.getElementById("period-select") as HTMLSelectElement | null;
      if (select) {
        select.onchange = () => {
          this.state.selectedPeriod = select.value as AppState["selectedPeriod"];
          this.render();
        };
      }
    }
  }

  navigateTo(tab: ActiveTab): void {
    this.state.activeTab = tab;
    this.state.isNavOpen = false;
    if (tab !== "capture") {
      if (this.voiceAutoTimeoutTimer) {
        clearTimeout(this.voiceAutoTimeoutTimer);
        this.voiceAutoTimeoutTimer = null;
      }
      if (this.speechRecognizer?.listening) {
        this.speechRecognizer.stop();
      }
    }
    if (tab === "investments") {
      void this.loadMarketDataForActiveSegment();
    }
    this.render();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async loadMarketDataForActiveSegment(): Promise<void> {
    const seg = this.state.selectedMarketSegment || "us";
    this.state.selectedMarketSegment = seg;
    try {
      this.currentMarketRecords = await loadMarketSegment(seg);
      if (this.state.activeTab === "investments") {
        this.render();
      }
    } catch {
      // Degradacion limpia
    }
  }

  toggleNav(): void {
    this.state.isNavOpen = !this.state.isNavOpen;
    this.render();
  }

  closeNav(): void {
    if (this.state.isNavOpen) {
      this.state.isNavOpen = false;
      this.render();
    }
  }

  changeCurrency(currency: "PEN" | "USD" | "EUR"): void {
    this.state.currency = currency;
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("finvoz_currency", currency);
    }
    this.showToast(`Moneda cambiada a ${currency}`, "info");
    this.render();
  }

  attemptAutoStartVoice(): void {
    if (typeof window === "undefined") return;

    if (!isSpeechRecognitionSupported()) {
      this.state.voiceState = "error";
      this.state.voiceErrorMessage =
        "Este navegador no soporta SpeechRecognition nativo. Puedes usar la entrada manual siempre disponible.";
      this.render();
      return;
    }

    const w = window as any;
    try {
      w.__finvoz_toggleVoiceRecognition?.();
    } catch {
      this.state.voiceState = "error";
      this.state.voiceErrorMessage =
        "El navegador requiere permiso o interacción previa para activar el micrófono. Conserva la opción manual.";
      this.render();
    }
  }

  private setupGlobalEventListeners(): void {
    const appEl = document.getElementById("app") || document.body;
    const w = window as any;

    // Delegación de clicks para todos los elementos data-action
    appEl.addEventListener("click", (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const actionEl = target.closest("[data-action]") as HTMLElement | null;
      if (!actionEl) return;

      const action = actionEl.getAttribute("data-action");
      if (!action) return;

      switch (action) {
        case "toggle-nav": {
          this.toggleNav();
          break;
        }
        case "close-nav": {
          this.closeNav();
          break;
        }
        case "navigate": {
          const tab = actionEl.getAttribute("data-tab") as ActiveTab;
          if (tab) this.navigateTo(tab);
          break;
        }
        case "toggle-theme": {
          this.toggleTheme();
          break;
        }
        case "set-theme": {
          const themeVal = actionEl.getAttribute("data-theme-val") as ThemeMode;
          if (themeVal) this.setTheme(themeVal);
          break;
        }
        case "filter-ledger": {
          const type = actionEl.getAttribute("data-type") as "income" | "expense";
          if (type) w.__finvoz_filterLedger?.(type);
          break;
        }
        case "switch-capture-mode": {
          const mode = actionEl.getAttribute("data-mode") as "voice" | "manual";
          if (mode) w.__finvoz_switchCaptureMode?.(mode);
          break;
        }
        case "toggle-voice-recognition": {
          w.__finvoz_toggleVoiceRecognition?.();
          break;
        }
        case "discard-proposal": {
          w.__finvoz_discardProposal?.();
          break;
        }
        case "confirm-voice-proposal": {
          w.__finvoz_confirmVoiceProposal?.();
          break;
        }
        case "export-csv": {
          w.__finvoz_exportCsv?.();
          break;
        }
        case "open-import-modal": {
          w.__finvoz_openImportModal?.();
          break;
        }
        case "close-import-modal": {
          w.__finvoz_closeImportModal?.();
          break;
        }
        case "execute-import": {
          this.showToast("Importación simulada con éxito", "success");
          w.__finvoz_closeImportModal?.();
          break;
        }
        case "reverse-entry": {
          const entryId = actionEl.getAttribute("data-entry-id");
          if (entryId) w.__finvoz_promptReverseEntry?.(entryId);
          break;
        }
        case "view-entry-detail": {
          const entryId = actionEl.getAttribute("data-entry-id");
          if (entryId) w.__finvoz_openEntryDetail?.(entryId);
          break;
        }
        case "close-entry-detail-modal": {
          w.__finvoz_closeEntryDetailModal?.();
          break;
        }
        case "open-new-account-modal": {
          w.__finvoz_openNewAccountModal?.();
          break;
        }
        case "close-new-account-modal": {
          w.__finvoz_closeNewAccountModal?.();
          break;
        }
        case "archive-account": {
          const accId = actionEl.getAttribute("data-account-id");
          if (accId) w.__finvoz_archiveAccount?.(accId);
          break;
        }
        case "open-opening-balance-modal": {
          w.__finvoz_openOpeningBalanceModal?.();
          break;
        }
        case "close-opening-balance-modal": {
          w.__finvoz_closeOpeningBalanceModal?.();
          break;
        }
        case "open-new-budget-modal": {
          w.__finvoz_openNewBudgetModal?.();
          break;
        }
        case "close-new-budget-modal": {
          w.__finvoz_closeNewBudgetModal?.();
          break;
        }
        case "delete-budget": {
          const budgetId = actionEl.getAttribute("data-budget-id");
          if (budgetId) w.__finvoz_deleteBudget?.(budgetId);
          break;
        }
        case "open-new-goal-modal": {
          w.__finvoz_openNewGoalModal?.();
          break;
        }
        case "close-new-goal-modal": {
          w.__finvoz_closeNewGoalModal?.();
          break;
        }
        case "refresh-quotes": {
          w.__finvoz_refreshQuotes?.();
          break;
        }
        case "open-manual-quote-modal": {
          w.__finvoz_openManualQuoteModal?.();
          break;
        }
        case "close-manual-quote-modal": {
          w.__finvoz_closeManualQuoteModal?.();
          break;
        }
        case "open-register-instrument-modal": {
          const symbol = actionEl.getAttribute("data-symbol") || undefined;
          const name = actionEl.getAttribute("data-name") || undefined;
          const currency = actionEl.getAttribute("data-currency") || undefined;
          w.__finvoz_openRegisterInstrumentModal?.(symbol, name, currency);
          break;
        }
        case "close-register-instrument-modal": {
          w.__finvoz_closeRegisterInstrumentModal?.();
          break;
        }
        case "quick-register-instrument": {
          const symbol = actionEl.getAttribute("data-symbol");
          const name = actionEl.getAttribute("data-name");
          const currency = actionEl.getAttribute("data-currency");
          w.__finvoz_openRegisterInstrumentModal?.(symbol, name, currency);
          break;
        }
        case "select-market-segment": {
          const seg = actionEl.getAttribute("data-segment") as MarketSegment;
          if (seg) {
            this.state.selectedMarketSegment = seg;
            this.state.marketSearchQuery = "";
            void this.loadMarketDataForActiveSegment();
          }
          break;
        }
        case "live-quote-symbol": {
          const sym = actionEl.getAttribute("data-symbol");
          const ex = actionEl.getAttribute("data-exchange") || undefined;
          if (sym) {
            void w.__finvoz_fetchLiveQuote?.(sym, ex);
          }
          break;
        }
        case "open-buy-modal": {
          const symbol = actionEl.getAttribute("data-instrument-id");
          const currency = actionEl.getAttribute("data-currency");
          const price = actionEl.getAttribute("data-price");
          w.__finvoz_openBuyModal?.(symbol, currency, price);
          break;
        }
        case "close-buy-modal": {
          w.__finvoz_closeBuyModal?.();
          break;
        }
        case "open-sell-modal": {
          const symbol = actionEl.getAttribute("data-instrument-id");
          const accountId = actionEl.getAttribute("data-account-id");
          w.__finvoz_openSellModal?.(symbol, accountId);
          break;
        }
        case "close-sell-modal": {
          w.__finvoz_closeSellModal?.();
          break;
        }
        case "archive-instrument-account": {
          const accountId = actionEl.getAttribute("data-account-id");
          if (accountId) w.__finvoz_archiveInstrumentAccount?.(accountId);
          break;
        }
        case "switch-sim-tab": {
          const simTab = actionEl.getAttribute("data-sim-tab") as "ci" | "loan" | "goal";
          if (simTab) w.__finvoz_switchSimTab?.(simTab);
          break;
        }
        case "connect-google": {
          w.__finvoz_connectGoogleOAuth?.();
          break;
        }
        case "link-sheet": {
          w.__finvoz_linkExistingSheet?.();
          break;
        }
        case "create-sheet": {
          w.__finvoz_createNewSheet?.();
          break;
        }
        case "export-movimientos": {
          w.__finvoz_exportMovimientos?.();
          break;
        }
        case "export-resumen": {
          w.__finvoz_exportResumen?.();
          break;
        }
        case "dismiss-quarantine": {
          w.__finvoz_dismissQuarantine?.();
          break;
        }
        case "install-pwa": {
          w.__finvoz_promptPwaInstall?.();
          break;
        }
        case "purge-local": {
          w.__finvoz_confirmPurgeLocal?.();
          break;
        }
        case "export-voice-diagnostics": {
          w.__finvoz_exportVoiceDiagnostics?.();
          break;
        }
        case "clear-voice-diagnostics": {
          w.__finvoz_clearVoiceDiagnostics?.();
          break;
        }
        case "open-new-profile-modal": {
          const m = document.getElementById("new-profile-modal");
          if (m) m.style.display = "flex";
          break;
        }
        case "close-new-profile-modal": {
          const m = document.getElementById("new-profile-modal");
          if (m) m.style.display = "none";
          break;
        }
        case "select-profile": {
          const profileId = actionEl.getAttribute("data-profile-id");
          if (profileId) w.__finvoz_selectProfile?.(profileId);
          break;
        }
        case "delete-profile": {
          const profileId = actionEl.getAttribute("data-profile-id");
          if (profileId) w.__finvoz_deleteProfile?.(profileId);
          break;
        }
        case "export-backup": {
          w.__finvoz_exportBackup?.();
          break;
        }
        case "open-import-backup-modal": {
          this.state.importBackupModalOpen = true;
          this.render();
          break;
        }
        case "close-import-backup-modal": {
          this.state.importBackupModalOpen = false;
          this.state.pendingImportPreview = null;
          this.render();
          break;
        }
        case "confirm-import-backup": {
          const mode = actionEl.getAttribute("data-import-mode") as ImportMode | null;
          if (mode) w.__finvoz_confirmImportBackup?.(mode);
          break;
        }
      }
    });

    // Delegación de change para selects interactivos y filtros
    appEl.addEventListener("change", (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      if (target.id && target.id.startsWith("filter-")) {
        w.__finvoz_filterLedgerTable?.();
      }
      if (target.id === "buy-instrument-select" || target.id === "buy-source-account") {
        w.__finvoz_onBuyInstrumentChange?.();
      }
      if (target.id === "sell-instrument-select" || target.id === "sell-dest-account") {
        w.__finvoz_onSellInstrumentChange?.();
      }

      const actionEl = target.closest("[data-action]") as HTMLElement | null;
      if (!actionEl) return;
      const action = actionEl.getAttribute("data-action");
      if (action === "change-currency") {
        const val = (actionEl as HTMLSelectElement).value as ("PEN" | "USD" | "EUR");
        if (val) this.changeCurrency(val);
      }
    });

    // Accesibilidad de teclado (WCAG 2.1 AA): Escape cierra el Drawer de navegación
    window.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Escape" && this.state.isNavOpen) {
        this.closeNav();
        document.getElementById("hamburger-btn")?.focus();
      }
    });

    // Delegación de submit para formularios
    appEl.addEventListener("submit", (e: Event) => {
      e.preventDefault();
      const form = (e.target as HTMLElement)?.closest("form[data-form]") as HTMLFormElement | null;
      if (!form) return;

      const formType = form.getAttribute("data-form");
      switch (formType) {
        case "manual-entry":
          w.__finvoz_submitManualEntry?.();
          break;
        case "new-account":
          w.__finvoz_submitNewAccount?.();
          break;
        case "opening-balance":
          w.__finvoz_submitOpeningBalance?.();
          break;
        case "new-budget":
          w.__finvoz_submitNewBudget?.();
          break;
        case "new-goal":
          w.__finvoz_submitNewGoal?.();
          break;
        case "manual-quote":
          w.__finvoz_submitManualQuote?.();
          break;
        case "new-profile":
          w.__finvoz_submitNewProfile?.();
          break;
        case "register-instrument":
          w.__finvoz_submitRegisterInstrument?.();
          break;
        case "buy-instrument":
          w.__finvoz_submitBuyInstrument?.();
          break;
        case "sell-instrument":
          w.__finvoz_submitSellInstrument?.();
          break;
      }
    });

    // Delegación de inputs (simuladores, transcripción, filtros texto/fecha, buscador catálogo)
    appEl.addEventListener("input", (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      if (target.id && target.id.startsWith("filter-")) {
        w.__finvoz_filterLedgerTable?.();
      } else if (target.id === "catalog-search-input") {
        w.__finvoz_filterCatalogTable?.();
      } else if (target.id === "buy-quantity" || target.id === "buy-price") {
        w.__finvoz_updateBuySummary?.();
      } else if (target.id === "sell-quantity" || target.id === "sell-price") {
        w.__finvoz_updateSellSummary?.();
      }

      const simType = target.getAttribute("data-sim-input");
      if (simType === "compound") {
        w.__finvoz_recalcCompound?.();
      } else if (simType === "loan") {
        w.__finvoz_recalcLoan?.();
      } else if (simType === "goal") {
        w.__finvoz_recalcGoalTime?.();
      }

      if (target.id === "voice-transcript-input") {
        w.__finvoz_onTranscriptInput?.((target as HTMLInputElement).value);
      }

      if (target.id === "filter-text" || target.id === "filter-start-date" || target.id === "filter-end-date") {
        w.__finvoz_filterLedgerTable?.();
      }
    });

    // Delegación de change (selects, settings inputs, import CSV)
    appEl.addEventListener("change", (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      if (target.id === "filter-account") {
        w.__finvoz_filterLedgerTable?.();
      }

      const setting = target.getAttribute("data-setting");
      if (setting === "google-client-id") {
        w.__finvoz_saveGoogleClientId?.((target as HTMLInputElement).value);
      } else if (setting === "spreadsheet-id") {
        w.__finvoz_saveSpreadsheetId?.((target as HTMLInputElement).value);
      }

      if (target.id === "period-select") {
        this.state.selectedPeriod = (target as HTMLSelectElement).value as AppState["selectedPeriod"];
        this.render();
      }

      if (target.id === "import-file-input") {
        const file = (target as HTMLInputElement).files?.[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = (re) => {
            const text = re.target?.result as string;
            const lines = text.split(/\r?\n/).filter(Boolean);
            const previewArea = document.getElementById("import-preview-area");
            const previewList = document.getElementById("import-preview-list");
            const confirmBtn = document.getElementById("btn-confirm-import");
            if (previewArea && previewList && confirmBtn) {
              previewArea.style.display = "block";
              previewList.innerHTML = lines.slice(0, 10).map((l) => `<div>${escapeHtml(l)}</div>`).join("");
              confirmBtn.style.display = "inline-block";
            }
          };
          reader.readAsText(file);
        }
      }

      if (target.id === "import-backup-file-input") {
        const file = (target as HTMLInputElement).files?.[0];
        if (file) w.__finvoz_onBackupFileSelected?.(file);
      }
    });
  }

  private bindGlobalHandlers(): void {
    const w = window as any;

    w.__finvoz_navigate = (tab: ActiveTab) => this.navigateTo(tab);

    w.__finvoz_filterLedger = (type: "income" | "expense") => {
      this.navigateTo("ledger");
      setTimeout(() => {
        const filterAcc = document.getElementById("filter-account") as HTMLSelectElement | null;
        if (filterAcc) {
          const matchingAcc = this.state.accounts.find((a) => a.type === type && !a.archived);
          if (matchingAcc) {
            filterAcc.value = matchingAcc.accountId;
            w.__finvoz_filterLedgerTable?.();
          }
        }
      }, 50);
    };

    // --- CAPTURA CON EXPERIENCIA VOICE-FIRST (FR-VOICE-1..12) ---
    const updateVoiceUI = (vState: VoiceState, message?: string, errorDetail?: string) => {
      this.state.voiceState = vState;
      if (errorDetail !== undefined) {
        this.state.voiceErrorMessage = errorDetail;
      } else if (vState !== "error") {
        this.state.voiceErrorMessage = null;
      }

      const statusBox = document.getElementById("voice-status-box");
      const badge = document.getElementById("voice-state-badge");
      const statusText = document.getElementById("voice-status-text");
      const startBtn = document.getElementById("btn-start-dictation");
      const errBanner = document.getElementById("voice-error-banner");
      const errDetail = document.getElementById("voice-error-detail");

      if (statusBox) {
        statusBox.className = `voice-status-box voice-state-${vState}`;
      }
      if (badge) {
        const labels: Record<VoiceState, string> = {
          idle: "Estado: Listo",
          listening: "🔴 Escuchando...",
          transcribing: "⚡ Transcribiendo...",
          confirming: "✅ Propuesta para Confirmar",
          error: "⚠️ Error / Micrófono",
        };
        badge.textContent = labels[vState] || vState;
      }
      if (statusText && message) {
        statusText.textContent = message;
      }
      if (startBtn) {
        if (vState === "listening") {
          startBtn.textContent = "⏹️ Detener Dictado";
          startBtn.classList.add("listening");
        } else {
          startBtn.textContent = "🎙️ Iniciar Dictado";
          startBtn.classList.remove("listening");
        }
      }
      if (errBanner) {
        if (vState === "error" || Boolean(this.state.voiceErrorMessage)) {
          errBanner.style.display = "block";
          if (errDetail && this.state.voiceErrorMessage) {
            errDetail.textContent = this.state.voiceErrorMessage;
          }
        } else {
          errBanner.style.display = "none";
        }
      }
    };

    w.__finvoz_switchCaptureMode = (mode: "voice" | "manual") => {
      const secVoice = document.getElementById("section-voice");
      const secManual = document.getElementById("section-manual");
      const btnVoice = document.getElementById("tab-btn-voice");
      const btnManual = document.getElementById("tab-btn-manual");

      if (mode === "voice") {
        if (secVoice) secVoice.style.display = "block";
        if (secManual) secManual.style.display = "none";
        btnVoice?.classList.replace("btn-secondary", "btn-primary");
        btnManual?.classList.replace("btn-primary", "btn-secondary");
      } else {
        if (secVoice) secVoice.style.display = "none";
        if (secManual) secManual.style.display = "block";
        btnManual?.classList.replace("btn-secondary", "btn-primary");
        btnVoice?.classList.replace("btn-primary", "btn-secondary");
        // Detener temporizador y micrófono si estaban activos
        if (this.voiceAutoTimeoutTimer) {
          clearTimeout(this.voiceAutoTimeoutTimer);
          this.voiceAutoTimeoutTimer = null;
        }
        if (this.speechRecognizer?.listening) {
          this.speechRecognizer.stop();
          updateVoiceUI("idle");
        }
      }
    };

    w.__finvoz_triggerVoiceError = (err: string) => {
      if (this.voiceAutoTimeoutTimer) {
        clearTimeout(this.voiceAutoTimeoutTimer);
        this.voiceAutoTimeoutTimer = null;
      }
      updateVoiceUI("error", "Incidencia en dictado. Redirigiendo a formulario manual...", err);
      this.showToast(err, "error");
      setTimeout(() => {
        w.__finvoz_switchCaptureMode?.("manual");
        this.showToast("Activado formulario manual como alternativa sin interrupciones", "info");
      }, 1200);
    };

    w.__finvoz_toggleVoiceRecognition = () => {
      if (this.speechRecognizer?.listening) {
        if (this.voiceAutoTimeoutTimer) {
          clearTimeout(this.voiceAutoTimeoutTimer);
          this.voiceAutoTimeoutTimer = null;
        }
        this.speechRecognizer.stop();
        updateVoiceUI("idle", "Dictado detenido por el usuario.");
        return;
      }

      if (!this.speechRecognizer) {
        const callbacks: SpeechRecognitionCallbacks = {
          onTranscript: (res) => {
            this.voiceHasReceivedTranscript = true;
            if (this.voiceAutoTimeoutTimer) {
              clearTimeout(this.voiceAutoTimeoutTimer);
              this.voiceAutoTimeoutTimer = null;
            }
            updateVoiceUI("transcribing", "Transcribiendo comando por voz...");
            const input = document.getElementById("voice-transcript-input") as HTMLInputElement | null;
            if (input) {
              input.value = res.transcript;
              w.__finvoz_onTranscriptInput(res.transcript);
            }
          },
          onError: (err) => {
            if (this.voiceAutoTimeoutTimer) {
              clearTimeout(this.voiceAutoTimeoutTimer);
              this.voiceAutoTimeoutTimer = null;
            }
            // Si el error es de permiso, interacción requerida o falta de soporte, mostrar aviso y conservar botón manual
            if (
              err.toLowerCase().includes("permiso") ||
              err.toLowerCase().includes("not-allowed") ||
              err.toLowerCase().includes("soporta")
            ) {
              const notice =
                "El navegador requiere permiso de micrófono o interacción previa. El botón de entrada manual sigue disponible.";
              updateVoiceUI("error", notice, notice);
              this.showToast(notice, "warning");
            } else {
              w.__finvoz_triggerVoiceError?.(err);
            }
          },
          onEnd: () => {
            if (this.voiceAutoTimeoutTimer) {
              clearTimeout(this.voiceAutoTimeoutTimer);
              this.voiceAutoTimeoutTimer = null;
            }
            if (this.state.voiceState === "listening") {
              updateVoiceUI("idle", "Reconocimiento finalizado. Toca el micrófono para iniciar.");
            }
          },
        };
        this.speechRecognizer = new BrowserSpeechRecognizer(callbacks);
      }

      const started = this.speechRecognizer.start("es-ES");
      if (started) {
        this.voiceHasReceivedTranscript = false;
        const curName =
          this.state.currency === "PEN" ? "soles" : this.state.currency === "USD" ? "dólares" : "euros";
        updateVoiceUI("listening", `Escuchando... Di una frase como: 'Gasté 12 ${curName} en café con la tarjeta'`);

        // Si no hay transcripción en 5 segundos, cambiar automáticamente a entrada manual (Requirement 3)
        if (this.voiceAutoTimeoutTimer) {
          clearTimeout(this.voiceAutoTimeoutTimer);
        }
        this.voiceAutoTimeoutTimer = setTimeout(() => {
          if (!this.voiceHasReceivedTranscript && this.state.activeTab === "capture") {
            if (this.speechRecognizer?.listening) {
              this.speechRecognizer.stop();
            }
            this.showToast("Sin transcripción en 5 segundos. Entrada manual activada.", "info");
            w.__finvoz_switchCaptureMode?.("manual");
          }
        }, 5000);
      } else {
        const errMsg =
          "El navegador requiere permiso o interacción previa para activar el micrófono. El botón de entrada manual sigue disponible.";
        updateVoiceUI("error", errMsg, errMsg);
      }
    };

    w.__finvoz_onTranscriptInput = (text: string) => {
      if (!text.trim()) {
        const container = document.getElementById("voice-proposal-container");
        if (container) container.style.display = "none";
        this.activeProposal = null;
        updateVoiceUI("idle", "Toca el micrófono y habla, o escribe tu frase en el campo inferior.");
        return;
      }

      updateVoiceUI("transcribing", "Analizando y estructurando asientos contables...");
      const proposal = parseFinancialExpression(text, this.state.accounts, this.state.currency);
      this.activeProposal = proposal;

      // Diagnóstico local de voz: registrar frases no reconocidas para dataset de pruebas del propietario (sin audio)
      if (!proposal.amountMinor || proposal.amountMinor <= 0n) {
        if (typeof localStorage !== "undefined") {
          try {
            const logs = JSON.parse(localStorage.getItem("finvoz_unrecognized_voice_logs") || "[]");
            logs.push({
              timestamp: new Date().toISOString(),
              text: text.trim(),
              currency: this.state.currency,
            });
            if (logs.length > 100) logs.splice(0, logs.length - 100);
            localStorage.setItem("finvoz_unrecognized_voice_logs", JSON.stringify(logs));
          } catch {
            // Ignorar fallos de cuota local
          }
        }
      }

      const container = document.getElementById("voice-proposal-container");
      if (!container) return;
      container.style.display = "block";
      updateVoiceUI("confirming", "Revisa la propuesta y pulsa 'Confirmar y Guardar Asiento' para registrarla.");

      const propDate = document.getElementById("prop-date") as HTMLInputElement | null;
      const propAmount = document.getElementById("prop-amount") as HTMLInputElement | null;
      const propDesc = document.getElementById("prop-desc") as HTMLInputElement | null;
      const propDebit = document.getElementById("prop-debit") as HTMLSelectElement | null;
      const propCredit = document.getElementById("prop-credit") as HTMLSelectElement | null;

      if (propDate) propDate.value = proposal.date;
      if (propAmount) propAmount.value = proposal.amountMinor ? (Number(proposal.amountMinor) / 100).toFixed(2) : "";
      if (propDesc) propDesc.value = proposal.description;
      if (propDebit && proposal.debitAccountId) propDebit.value = proposal.debitAccountId;
      if (propCredit && proposal.creditAccountId) propCredit.value = proposal.creditAccountId;
    };

    w.__finvoz_discardProposal = () => {
      this.activeProposal = null;
      const container = document.getElementById("voice-proposal-container");
      if (container) container.style.display = "none";
      const input = document.getElementById("voice-transcript-input") as HTMLInputElement | null;
      if (input) input.value = "";
      updateVoiceUI("idle", "Propuesta descartada. Listo para una nueva operación.");
    };

    w.__finvoz_confirmVoiceProposal = async () => {
      const propDate = (document.getElementById("prop-date") as HTMLInputElement)?.value;
      const propAmountStr = (document.getElementById("prop-amount") as HTMLInputElement)?.value;
      const propDesc = (document.getElementById("prop-desc") as HTMLInputElement)?.value;
      const propDebit = (document.getElementById("prop-debit") as HTMLSelectElement)?.value;
      const propCredit = (document.getElementById("prop-credit") as HTMLSelectElement)?.value;

      if (!propDate || !propAmountStr || !propDesc || !propDebit || !propCredit) {
        this.showToast("Completa todos los campos antes de confirmar", "warning");
        return;
      }

      if (propDebit === propCredit) {
        this.showToast("Las cuentas de Debe y Haber no pueden ser iguales", "warning");
        return;
      }

      const amountNum = parseFloat(propAmountStr);
      if (isNaN(amountNum) || amountNum <= 0) {
        this.showToast("El importe debe ser mayor que cero", "warning");
        return;
      }

      const amountMinor = BigInt(Math.round(amountNum * 100));

      await this.confirmNewEntry({
        date: propDate,
        description: propDesc,
        origin: "voice",
        debitAccountId: propDebit,
        creditAccountId: propCredit,
        amountMinor,
        currency: this.activeProposal?.currency || this.state.currency,
        scale: 2,
      });

      w.__finvoz_discardProposal();
      updateVoiceUI("idle", "¡Asiento confirmado y registrado con éxito!");
    };

    w.__finvoz_submitManualEntry = async () => {
      const date = (document.getElementById("manual-date") as HTMLInputElement)?.value;
      const amountStr = (document.getElementById("manual-amount") as HTMLInputElement)?.value;
      const description = (document.getElementById("manual-desc") as HTMLInputElement)?.value;
      const debitAccountId = (document.getElementById("manual-debit") as HTMLSelectElement)?.value;
      const creditAccountId = (document.getElementById("manual-credit") as HTMLSelectElement)?.value;

      if (!date || !amountStr || !description || !debitAccountId || !creditAccountId) {
        this.showToast("Todos los campos son obligatorios", "warning");
        return;
      }

      if (debitAccountId === creditAccountId) {
        this.showToast("La cuenta de cargo y abono deben ser distintas", "warning");
        return;
      }

      const amountNum = parseFloat(amountStr);
      if (isNaN(amountNum) || amountNum <= 0) {
        this.showToast("Importe no válido", "warning");
        return;
      }

      const amountMinor = BigInt(Math.round(amountNum * 100));

      await this.confirmNewEntry({
        date,
        description,
        origin: "manual",
        debitAccountId,
        creditAccountId,
        amountMinor,
        currency: this.state.currency,
        scale: 2,
      });

      (document.getElementById("manual-entry-form") as HTMLFormElement)?.reset();
    };

    // --- LIBRO DIARIO ---
    w.__finvoz_filterLedgerTable = () => {
      const text = (document.getElementById("filter-text") as HTMLInputElement)?.value.toLowerCase().trim() || "";
      const accountId = (document.getElementById("filter-account") as HTMLSelectElement)?.value || "";
      const filterCurrency = (document.getElementById("filter-currency") as HTMLSelectElement)?.value || "";
      const startDate = (document.getElementById("filter-start-date") as HTMLInputElement)?.value || "";
      const endDate = (document.getElementById("filter-end-date") as HTMLInputElement)?.value || "";

      const tbody = document.getElementById("ledger-tbody");
      if (!tbody) return;

      const rows = tbody.querySelectorAll("tr");
      rows.forEach((tr) => {
        const entryId = tr.getAttribute("data-entry-id");
        if (!entryId) return;
        const entry = this.state.entries.find((e) => e.entry.entryId === entryId)?.entry;
        if (!entry) return;

        let visible = true;
        if (text && !entry.description.toLowerCase().includes(text) && !entry.entryId.toLowerCase().includes(text)) {
          visible = false;
        }
        if (accountId && !entry.postings.some((p) => p.accountId === accountId)) {
          visible = false;
        }
        if (filterCurrency && !entry.postings.some((p) => p.currency === filterCurrency)) {
          visible = false;
        }
        if (startDate && entry.date < startDate) visible = false;
        if (endDate && entry.date > endDate) visible = false;

        tr.style.display = visible ? "" : "none";
      });
    };

    w.__finvoz_openEntryDetail = (entryId: string) => {
      const chained = this.state.entries.find((e) => e.entry.entryId === entryId);
      if (!chained) return;

      const e = chained.entry;
      const modal = document.getElementById("entry-detail-modal");
      const body = document.getElementById("entry-detail-body");
      const actions = document.getElementById("detail-modal-actions");
      if (!modal || !body || !actions) return;

      const isReversal = Boolean(e.reversesEntryId);
      const isAlreadyReversed = this.state.entries.some((c) => c.entry.reversesEntryId === e.entryId);
      const accountNames = new Map(this.state.accounts.map((a) => [a.accountId, a.name]));

      body.innerHTML = `
        <div style="font-size: 0.85rem; display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 16px; background-color: var(--bg-tertiary); padding: 12px; border-radius: 8px;">
          <div><strong>ID Asiento:</strong> <span style="font-family: var(--font-mono); font-size: 0.8rem;">${escapeHtml(e.entryId)}</span></div>
          <div><strong>Fecha:</strong> ${escapeHtml(e.date)}</div>
          <div><strong>Descripción:</strong> ${escapeHtml(e.description)}</div>
          <div><strong>Origen:</strong> <span class="badge badge-${escapeAttr(e.origin)}">${escapeHtml(e.origin)}</span></div>
          <div><strong>Secuencia Dispositivo:</strong> #${chained.deviceSeq}</div>
          <div><strong>Registrado:</strong> ${new Date(chained.createdAt).toLocaleString()}</div>
          <div style="grid-column: span 2;"><strong>Hash Entrada:</strong> <span style="font-family: var(--font-mono); font-size: 0.7rem; word-break: break-all;">${escapeHtml(chained.entryHash)}</span></div>
          ${chained.deviceChainHash ? `<div style="grid-column: span 2;"><strong>Hash de Cadena:</strong> <span style="font-family: var(--font-mono); font-size: 0.7rem; word-break: break-all;">${escapeHtml(chained.deviceChainHash)}</span></div>` : ""}
          ${isReversal ? `<div style="grid-column: span 2; color: var(--danger);"><strong>Reversión del asiento:</strong> <span style="font-family: var(--font-mono);">${escapeHtml(e.reversesEntryId!)}</span></div>` : ""}
          ${isAlreadyReversed ? `<div style="grid-column: span 2; color: var(--warning-text); font-weight: 600;">⚠️ Este asiento ha sido revertido posteriormente en el libro diario.</div>` : ""}
        </div>

        <div style="font-weight: 600; margin-bottom: 6px; font-size: 0.9rem;">Apuntes Contables (Partida Doble INV-1, INV-2):</div>
        <div class="table-container" style="max-height: 220px; overflow-y: auto;">
          <table style="font-size: 0.85rem;">
            <thead>
              <tr>
                <th>Cuenta</th>
                <th>Tipo</th>
                <th>Importe</th>
                <th>Cantidad / Instrumento</th>
              </tr>
            </thead>
            <tbody>
              ${e.postings.map((p) => {
                const accName = accountNames.get(p.accountId) ?? p.accountId;
                const isDebit = p.amountMinor > 0n;
                const displayAmt = isDebit ? p.amountMinor : -p.amountMinor;
                return `
                  <tr>
                    <td>
                      <div style="font-weight: 500;">${escapeHtml(accName)}</div>
                      <div style="font-size: 0.7rem; color: var(--text-muted); font-family: var(--font-mono);">${escapeHtml(p.accountId)}</div>
                    </td>
                    <td>
                      <span class="badge" style="background-color: ${isDebit ? 'var(--accent-light)' : 'var(--bg-tertiary)'}; color: ${isDebit ? 'var(--accent)' : 'var(--text-primary)'}; font-weight: 600;">
                        ${isDebit ? 'Debe (+)' : 'Haber (-)'}
                      </span>
                    </td>
                    <td style="font-family: var(--font-mono); font-weight: 600;">
                      ${formatMinorUnits(displayAmt, p.currency, p.scale)}
                    </td>
                    <td style="font-family: var(--font-mono);">
                      ${p.quantityMinor !== null ? `${escapeHtml(p.quantityMinor.toString())} uds. (${escapeHtml(p.instrumentId || '—')})` : '—'}
                    </td>
                  </tr>
                `;
              }).join("")}
            </tbody>
          </table>
        </div>
      `;

      actions.innerHTML = "";
      if (!isReversal && !isAlreadyReversed) {
        const revBtn = document.createElement("button");
        revBtn.className = "btn-secondary";
        revBtn.style.color = "var(--danger)";
        revBtn.textContent = "↺ Revertir Asiento (INV-3)";
        revBtn.setAttribute("data-action", "reverse-entry");
        revBtn.setAttribute("data-entry-id", e.entryId);
        actions.appendChild(revBtn);
      }

      modal.style.display = "flex";
    };

    w.__finvoz_closeEntryDetailModal = () => {
      const modal = document.getElementById("entry-detail-modal");
      if (modal) modal.style.display = "none";
    };

    w.__finvoz_promptReverseEntry = async (entryId: string) => {
      const chained = this.state.entries.find((e) => e.entry.entryId === entryId);
      if (!chained) return;

      // Bloquear si el asiento seleccionado ya es una reversión (INV-3, FR-LEDGER-3)
      if (chained.entry.reversesEntryId) {
        this.showToast("No se puede revertir un asiento que ya es una reversión", "warning");
        return;
      }

      // Bloquear si el asiento ya ha sido revertido previamente por otro asiento
      const alreadyReversed = this.state.entries.some((c) => c.entry.reversesEntryId === entryId);
      if (alreadyReversed) {
        this.showToast("Este asiento ya ha sido revertido previamente", "warning");
        return;
      }

      const confirmed = window.confirm(
        `¿Deseas revertir el asiento "${chained.entry.description}" (${entryId})? FinVoz es inmutable (INV-3): se creará un asiento equilibrado de reversión (FR-LEDGER-3).`
      );
      if (!confirmed) return;

      const today = new Date().toISOString().slice(0, 10);
      const rev = reverseEntry(chained.entry, { date: today });

      const { chained: newChained, nextState } = await appendChainedEntry(
        rev,
        this.deviceChainState,
        new Date().toISOString()
      );

      await this.localStore.putBatch("entries", [{ id: newChained.entry.entryId, value: newChained }]);
      // Sin encolar en Outbox: nada la drena en esta fase (sincronización automática
      // diferida a APK, reviews/CODEX.md "Auditoría del cambio local-first" punto 3).
      // Encolar sin nunca procesar produciría una cola que crece sin fin — residuo
      // engañoso, no infraestructura útil.
      this.deviceChainState = nextState;
      await this.localStore.writeMeta("device_chain_state", nextState);

      this.state.entries.push(newChained);

      this.showToast(`Asiento revertido con éxito mediante #${newChained.deviceSeq}`, "success");
      this.render();
    };

    w.__finvoz_exportCsv = () => {
      if (this.state.entries.length === 0) {
        this.showToast("No hay asientos para exportar", "warning");
        return;
      }
      const headers = ["entry_id", "date", "description", "origin", "reverses_entry_id", "postings"];
      const rows = this.state.entries.map((c) => [
        c.entry.entryId,
        c.entry.date,
        `"${c.entry.description.replace(/"/g, '""')}"`,
        c.entry.origin,
        c.entry.reversesEntryId ?? "",
        `"${JSON.stringify(c.entry.postings).replace(/"/g, '""')}"`,
      ]);
      const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `finvoz-libro-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    };

    w.__finvoz_openImportModal = () => {
      const modal = document.getElementById("import-modal");
      if (modal) modal.style.display = "flex";
    };

    w.__finvoz_closeImportModal = () => {
      const modal = document.getElementById("import-modal");
      if (modal) modal.style.display = "none";
    };

    // --- CUENTAS ---
    w.__finvoz_openNewAccountModal = () => {
      const modal = document.getElementById("new-account-modal");
      if (modal) modal.style.display = "flex";
    };
    w.__finvoz_closeNewAccountModal = () => {
      const modal = document.getElementById("new-account-modal");
      if (modal) modal.style.display = "none";
    };

    w.__finvoz_submitNewAccount = async () => {
      const name = (document.getElementById("new-acc-name") as HTMLInputElement)?.value.trim();
      const type = (document.getElementById("new-acc-type") as HTMLSelectElement)?.value as any;
      const parentId = (document.getElementById("new-acc-parent") as HTMLSelectElement)?.value || null;
      const instrumentId = (document.getElementById("new-acc-instrument") as HTMLInputElement)?.value.trim() || null;

      if (!name) return;

      const accountId = `acc-${generateEntryId().slice(0, 8)}`;
      const newAcc = { accountId, name, type, currency: this.state.currency, parentId, archived: false, instrumentId };

      await this.localStore.putBatch("accounts", [{ id: accountId, value: newAcc }]);
      this.state.accounts.push(newAcc);
      this.showToast(`Cuenta "${name}" creada`, "success");
      w.__finvoz_closeNewAccountModal();
      this.render();
    };

    w.__finvoz_archiveAccount = async (accountId: string) => {
      const acc = this.state.accounts.find((a) => a.accountId === accountId);
      if (!acc) return;
      const confirmed = window.confirm(`¿Archivar la cuenta "${acc.name}"? Se conservará en el histórico pero no aparecerá para nuevos asientos (FR-ACCT-5).`);
      if (!confirmed) return;

      const updated = { ...acc, archived: true };
      await this.localStore.putBatch("accounts", [{ id: accountId, value: updated }]);
      const idx = this.state.accounts.findIndex((a) => a.accountId === accountId);
      if (idx >= 0) this.state.accounts[idx] = updated;

      this.showToast(`Cuenta "${acc.name}" archivada`, "info");
      this.render();
    };

    w.__finvoz_openOpeningBalanceModal = () => {
      const modal = document.getElementById("opening-balance-modal");
      if (modal) modal.style.display = "flex";
    };
    w.__finvoz_closeOpeningBalanceModal = () => {
      const modal = document.getElementById("opening-balance-modal");
      if (modal) modal.style.display = "none";
    };

    w.__finvoz_submitOpeningBalance = async () => {
      const accountId = (document.getElementById("ob-account") as HTMLSelectElement)?.value;
      const amountStr = (document.getElementById("ob-amount") as HTMLInputElement)?.value;
      const date = (document.getElementById("ob-date") as HTMLInputElement)?.value;

      const num = parseFloat(amountStr);
      if (!accountId || isNaN(num) || num <= 0 || !date) return;

      const amountMinor = BigInt(Math.round(num * 100));

      const acc = this.state.accounts.find((a) => a.accountId === accountId);
      const currency = acc?.currency || this.state.currency;

      await this.confirmNewEntry({
        date,
        description: "Saldo de apertura inicial",
        origin: "manual",
        debitAccountId: accountId,
        creditAccountId: "acc-equity-initial",
        amountMinor,
        currency,
        scale: 2,
      });

      w.__finvoz_closeOpeningBalanceModal();
    };

    // --- PRESUPUESTOS Y METAS ---
    w.__finvoz_openNewBudgetModal = () => {
      const m = document.getElementById("new-budget-modal");
      if (m) m.style.display = "flex";
    };
    w.__finvoz_closeNewBudgetModal = () => {
      const m = document.getElementById("new-budget-modal");
      if (m) m.style.display = "none";
    };

    w.__finvoz_submitNewBudget = async () => {
      const accountId = (document.getElementById("bg-account") as HTMLSelectElement)?.value;
      const limitStr = (document.getElementById("bg-limit") as HTMLInputElement)?.value;
      const thresholdPct = Number((document.getElementById("bg-threshold") as HTMLInputElement)?.value) || 80;
      const period = (document.getElementById("bg-period") as HTMLSelectElement)?.value as any;

      const limitNum = parseFloat(limitStr);
      if (!accountId || isNaN(limitNum) || limitNum <= 0) return;

      const budgetId = `bg-${generateEntryId().slice(0, 8)}`;
      const newBudget: Budget = {
        budgetId,
        accountId,
        limitMinor: BigInt(Math.round(limitNum * 100)),
        currency: this.state.currency,
        scale: 2,
        period,
        thresholdPct,
        archived: false,
      };

      await this.localStore.putBatch("budgets_goals", [{ id: budgetId, value: newBudget }]);
      this.state.budgets.push(newBudget);
      this.showToast("Presupuesto guardado", "success");
      w.__finvoz_closeNewBudgetModal();
      this.render();
    };

    w.__finvoz_deleteBudget = async (budgetId: string) => {
      await this.localStore.delete("budgets_goals", budgetId);
      this.state.budgets = this.state.budgets.filter((b) => b.budgetId !== budgetId);
      this.showToast("Presupuesto eliminado", "info");
      this.render();
    };

    w.__finvoz_openNewGoalModal = () => {
      const m = document.getElementById("new-goal-modal");
      if (m) m.style.display = "flex";
    };
    w.__finvoz_closeNewGoalModal = () => {
      const m = document.getElementById("new-goal-modal");
      if (m) m.style.display = "none";
    };

    w.__finvoz_submitNewGoal = async () => {
      const name = (document.getElementById("goal-name") as HTMLInputElement)?.value.trim();
      const accountId = (document.getElementById("goal-account") as HTMLSelectElement)?.value;
      const targetStr = (document.getElementById("goal-target") as HTMLInputElement)?.value;
      const targetDate = (document.getElementById("goal-date") as HTMLInputElement)?.value;

      const targetNum = parseFloat(targetStr);
      if (!name || !accountId || isNaN(targetNum) || targetNum <= 0 || !targetDate) return;

      const goalId = `goal-${generateEntryId().slice(0, 8)}`;
      const newGoal: Goal = {
        goalId,
        name,
        accountId,
        targetMinor: BigInt(Math.round(targetNum * 100)),
        currency: this.state.currency,
        scale: 2,
        targetDate,
        archived: false,
      };

      await this.localStore.putBatch("budgets_goals", [{ id: goalId, value: newGoal }]);
      this.state.goals.push(newGoal);
      this.showToast(`Meta "${name}" creada`, "success");
      w.__finvoz_closeNewGoalModal();
      this.render();
    };

    // --- INVERSIONES (FR-INVEST-1..8, FR-MKT-1..8) ---
    w.__finvoz_filterCatalogTable = () => {
      const query = (document.getElementById("catalog-search-input") as HTMLInputElement)?.value.toLowerCase().trim() || "";
      this.state.marketSearchQuery = query;
      const tbody = document.getElementById("catalog-tbody");
      if (!tbody) return;
      const rows = Array.from(tbody.querySelectorAll("tr"));
      let matchesCount = 0;
      rows.forEach((row) => {
        if (row.id === "catalog-no-matches-row") return;
        const sym = (row.getAttribute("data-catalog-symbol") || "").toLowerCase();
        const name = (row.getAttribute("data-catalog-name") || "").toLowerCase();
        const cat = (row.getAttribute("data-catalog-type") || "").toLowerCase();
        const matches = !query || sym.includes(query) || name.includes(query) || cat.includes(query);
        row.style.display = matches ? "" : "none";
        if (matches) matchesCount++;
      });

      // Si el usuario busca, consultar en el dataset cargado o en los benchmarks del catálogo
      if (query) {
        let fullMatches = this.currentMarketRecords && this.currentMarketRecords.length > 0
          ? searchMarketInstruments(this.currentMarketRecords, query, 40)
          : [];

        if (fullMatches.length === 0) {
          const catalogAsRecords: MarketInstrumentRecord[] = INVESTMENT_CATALOG.map((c) => ({
            s: c.symbol,
            n: c.name,
            e: c.currency === "PEN" ? "BVL" : "US",
            t: c.category === "ETF" ? "ETF" : "Stock",
            c: c.currency === "PEN" ? "PE" : "US",
            i: c.isin,
          }));
          fullMatches = searchMarketInstruments(catalogAsRecords, query, 10);
        }

        const existingSymbols = new Set(
          rows.map((r) => (r.getAttribute("data-catalog-symbol") || "").toUpperCase()),
        );
        for (const item of fullMatches) {
          if (!existingSymbols.has(item.s.toUpperCase())) {
            const newRow = document.createElement("tr");
            newRow.setAttribute("data-catalog-symbol", item.s);
            newRow.setAttribute("data-catalog-name", item.n.toLowerCase());
            newRow.setAttribute("data-catalog-type", item.t.toLowerCase());
            const quote = this.quotes.find((q) => q.instrumentId === item.s);
            const activeSeg = this.state.selectedMarketSegment || "us";
            const itemCur = quote?.currency || (activeSeg === "latam" && (item.s.endsWith("C1") || item.e === "BVL") ? "PEN" : "USD");
            const priceHtml = quote
              ? `<div>${formatMinorUnits(quote.priceMinor, itemCur, quote.scale)}</div>`
              : `<button type="button" class="btn-secondary" style="font-size: 0.75rem; padding: 2px 6px;" data-action="live-quote-symbol" data-symbol="${escapeAttr(item.s)}" data-exchange="${escapeAttr(item.e)}">⚡ Cotizar</button>`;

            newRow.innerHTML = `
              <td>
                <span style="font-weight: 700; font-family: var(--font-mono);">${escapeHtml(item.s)}</span>
                ${item.i ? `<div style="font-size: 0.7rem; color: var(--text-muted); font-family: var(--font-mono);">${escapeHtml(item.i)}</div>` : ""}
              </td>
              <td style="font-weight: 500;">${escapeHtml(item.n)}</td>
              <td><span class="badge" style="background-color: var(--bg-tertiary); color: var(--text-primary); font-size: 0.75rem;">${escapeHtml(item.e)}</span></td>
              <td><span class="badge" style="font-size: 0.75rem;">${escapeHtml(item.t)}</span></td>
              <td><span class="badge" style="font-family: var(--font-mono);">${escapeHtml(itemCur)}</span></td>
              <td style="font-family: var(--font-mono); font-weight: 600;">${priceHtml}</td>
              <td style="font-size: 0.8rem; font-family: var(--font-mono); color: var(--text-secondary);">${formatVolume(quote?.volume)}</td>
              <td style="font-size: 0.8rem; color: var(--text-secondary);"><div>Yahoo Finance</div><div style="font-size: 0.7rem; color: var(--text-muted);">As-Of: ${new Date().toISOString().slice(0, 10)}</div></td>
              <td style="text-align: right; white-space: nowrap;">
                <div style="display: inline-flex; gap: 4px;">
                  <button type="button" class="btn-secondary" style="font-size: 0.75rem; padding: 4px 8px;" data-action="quick-register-instrument" data-symbol="${escapeAttr(item.s)}" data-name="${escapeAttr(item.n)}" data-currency="${escapeAttr(itemCur)}">➕ Registrar</button>
                  <button type="button" class="btn-primary" style="font-size: 0.75rem; padding: 4px 10px;" data-action="open-buy-modal" data-instrument-id="${escapeAttr(item.s)}" data-currency="${escapeAttr(itemCur)}" data-price="${quote ? (Number(quote.priceMinor) / 100).toFixed(2) : ""}">🛒 Comprar</button>
                </div>
              </td>
            `;
            tbody.appendChild(newRow);
            matchesCount++;
          }
        }
      }

      let noMatchesRow = document.getElementById("catalog-no-matches-row");
      if (matchesCount === 0 && query) {
        if (!noMatchesRow) {
          noMatchesRow = document.createElement("tr");
          noMatchesRow.id = "catalog-no-matches-row";
          tbody.appendChild(noMatchesRow);
        }
        noMatchesRow.style.display = "";
        noMatchesRow.innerHTML = `
          <td colspan="9" style="text-align: center; padding: 24px; color: var(--text-secondary);">
            No se encontró "${escapeHtml(query)}" en el catálogo activo.<br/>
            <button class="btn-primary" style="margin-top: 8px; font-size: 0.85rem;" data-action="open-register-instrument-modal" data-symbol="${escapeAttr(query.toUpperCase())}">
              ➕ Registrar "${escapeHtml(query.toUpperCase())}" en tu inventario ahora
            </button>
          </td>
        `;
      } else if (noMatchesRow) {
        noMatchesRow.style.display = "none";
      }
    };

    w.__finvoz_fetchLiveQuote = async (symbol: string, exchange?: string) => {
      const querySymbol = getYahooTicker(symbol, exchange);
      this.showToast(`Consultando cotización en tiempo real de ${symbol}${querySymbol !== symbol ? ` (${querySymbol})` : ""}...`, "info");
      try {
        const today = new Date().toISOString().slice(0, 10);
        const quote = await this.quoteProvider.getQuote({ instrumentId: querySymbol, asOf: today });
        if (quote) {
          // Normalizar para registrar la cotización bajo el símbolo del catálogo
          const normalizedQuote: PriceQuote = { ...quote, instrumentId: symbol };
          const idx = this.quotes.findIndex((q) => q.instrumentId === symbol);
          if (idx >= 0) {
            this.quotes[idx] = normalizedQuote;
          } else {
            this.quotes.push(normalizedQuote);
          }
          if (querySymbol !== symbol) {
            const idxQ = this.quotes.findIndex((q) => q.instrumentId === querySymbol);
            if (idxQ >= 0) {
              this.quotes[idxQ] = quote;
            } else {
              this.quotes.push(quote);
            }
          }
          const formatted = formatMinorUnits(quote.priceMinor, quote.currency, quote.scale);
          const volStr = quote.volume ? ` | Vol: ${formatVolume(quote.volume)}` : "";
          this.showToast(`${symbol}: ${formatted}${volStr} (Yahoo Finance)`, "success");
          this.render();
        } else {
          this.showToast(`No se obtuvo respuesta de red para ${symbol}. Puedes ingresar el precio manual.`, "warning");
        }
      } catch {
        this.showToast(`Fallo al consultar cotización para ${symbol}`, "error");
      }
    };

    w.__finvoz_openRegisterInstrumentModal = (symbol?: string, name?: string, currency?: string) => {
      const modal = document.getElementById("register-instrument-modal");
      if (!modal) return;
      const symInput = document.getElementById("reg-symbol") as HTMLInputElement | null;
      const nameInput = document.getElementById("reg-name") as HTMLInputElement | null;
      const curSelect = document.getElementById("reg-currency") as HTMLSelectElement | null;
      if (symInput) symInput.value = symbol || "";
      if (nameInput) nameInput.value = name || "";
      if (curSelect && currency) curSelect.value = currency;
      modal.style.display = "flex";
    };

    w.__finvoz_closeRegisterInstrumentModal = () => {
      const modal = document.getElementById("register-instrument-modal");
      if (modal) modal.style.display = "none";
    };

    w.__finvoz_submitRegisterInstrument = async () => {
      const symbol = (document.getElementById("reg-symbol") as HTMLInputElement)?.value.trim().toUpperCase();
      const name = (document.getElementById("reg-name") as HTMLInputElement)?.value.trim();
      const instCurrency = (document.getElementById("reg-currency") as HTMLSelectElement)?.value || this.state.currency;

      if (!symbol || !name) {
        this.showToast("El símbolo y nombre son obligatorios", "warning");
        return;
      }

      const existing = this.state.accounts.find(
        (a) => a.type === "asset" && a.instrumentId === symbol && !a.archived
      );
      if (existing) {
        this.showToast(`El instrumento ${symbol} ya está registrado`, "info");
        w.__finvoz_closeRegisterInstrumentModal();
        return;
      }

      const accountId = `acc-inv-${symbol.toLowerCase().replace(/[^a-z0-9]/g, "")}-${generateEntryId().slice(0, 4)}`;
      const newAccount = {
        accountId,
        name,
        type: "asset" as const,
        currency: instCurrency,
        parentId: null,
        archived: false,
        instrumentId: symbol,
      };

      await this.localStore.putBatch("accounts", [{ id: accountId, value: newAccount }]);
      this.state.accounts.push(newAccount);

      // NO se genera ningún asiento contable ni cambio de saldo (requerimiento 2a)
      this.showToast(`Instrumento ${symbol} registrado en inventario`, "success");
      w.__finvoz_closeRegisterInstrumentModal();
      this.render();
    };

    w.__finvoz_openBuyModal = (instrumentId?: string, currency?: string, price?: string) => {
      const modal = document.getElementById("buy-instrument-modal");
      if (!modal) return;

      const sel = document.getElementById("buy-instrument-select") as HTMLSelectElement | null;
      if (sel && instrumentId) {
        sel.value = instrumentId;
      }

      const priceInput = document.getElementById("buy-price") as HTMLInputElement | null;
      if (priceInput) {
        if (price) {
          priceInput.value = price;
        } else if (sel) {
          const selectedOpt = sel.options[sel.selectedIndex];
          const p = selectedOpt?.getAttribute("data-price");
          if (p) priceInput.value = p;
          else priceInput.value = "";
        }
      }

      const qtyInput = document.getElementById("buy-quantity") as HTMLInputElement | null;
      if (qtyInput && !qtyInput.value) {
        qtyInput.value = "1";
      }

      modal.style.display = "flex";
      w.__finvoz_onBuyInstrumentChange?.();
      w.__finvoz_updateBuySummary?.();
    };

    w.__finvoz_closeBuyModal = () => {
      const modal = document.getElementById("buy-instrument-modal");
      if (modal) modal.style.display = "none";
    };

    w.__finvoz_onBuyInstrumentChange = () => {
      const sel = document.getElementById("buy-instrument-select") as HTMLSelectElement | null;
      if (!sel) return;
      const selectedOpt = sel.options[sel.selectedIndex];
      if (selectedOpt) {
        const p = selectedOpt.getAttribute("data-price");
        const priceInput = document.getElementById("buy-price") as HTMLInputElement | null;
        if (priceInput && p && !priceInput.value) {
          priceInput.value = p;
        }
      }
      w.__finvoz_updateBuySummary?.();
    };

    w.__finvoz_updateBuySummary = () => {
      const qty = parseFloat((document.getElementById("buy-quantity") as HTMLInputElement)?.value) || 0;
      const price = parseFloat((document.getElementById("buy-price") as HTMLInputElement)?.value) || 0;
      const totalEl = document.getElementById("buy-total-amount");
      const feedbackEl = document.getElementById("buy-balance-feedback");
      const confirmBtn = document.getElementById("btn-confirm-buy") as HTMLButtonElement | null;
      const sourceSel = document.getElementById("buy-source-account") as HTMLSelectElement | null;

      const total = qty * price;
      const sourceOpt = sourceSel?.options[sourceSel.selectedIndex];
      const sourceCur = sourceOpt?.getAttribute("data-currency") || this.state.currency;
      const balanceMinor = BigInt(sourceOpt?.getAttribute("data-balance-minor") || "0");
      const totalMinor = BigInt(Math.round(total * 100));

      if (totalEl) {
        totalEl.textContent = formatMinorUnits(totalMinor, sourceCur, 2);
      }

      if (qty <= 0 || price <= 0) {
        if (feedbackEl) feedbackEl.innerHTML = `<span style="color: var(--warning);">Introduce una cantidad y precio válidos.</span>`;
        if (confirmBtn) confirmBtn.disabled = true;
        return;
      }

      if (balanceMinor < totalMinor) {
        if (feedbackEl) {
          feedbackEl.innerHTML = `<span style="color: var(--danger); font-weight: 600;">⚠️ Saldo insuficiente: disponible ${formatMinorUnits(balanceMinor, sourceCur, 2)}, requerido ${formatMinorUnits(totalMinor, sourceCur, 2)}</span>`;
        }
        if (confirmBtn) confirmBtn.disabled = true;
      } else {
        if (feedbackEl) {
          feedbackEl.innerHTML = `<span style="color: var(--success); font-weight: 600;">✓ Saldo suficiente (${formatMinorUnits(balanceMinor, sourceCur, 2)} disponible)</span>`;
        }
        if (confirmBtn) confirmBtn.disabled = false;
      }
    };

    w.__finvoz_submitBuyInstrument = async () => {
      const sel = document.getElementById("buy-instrument-select") as HTMLSelectElement | null;
      const qtyInput = document.getElementById("buy-quantity") as HTMLInputElement | null;
      const priceInput = document.getElementById("buy-price") as HTMLInputElement | null;
      const sourceSel = document.getElementById("buy-source-account") as HTMLSelectElement | null;

      if (!sel || !qtyInput || !priceInput || !sourceSel) return;

      const symbol = sel.value;
      const selectedOpt = sel.options[sel.selectedIndex];
      const optName = selectedOpt?.getAttribute("data-name") || symbol;
      const optCur = selectedOpt?.getAttribute("data-currency") || this.state.currency;
      const qty = parseInt(qtyInput.value, 10);
      const price = parseFloat(priceInput.value);
      const sourceAccountId = sourceSel.value;

      if (!symbol || isNaN(qty) || qty <= 0 || isNaN(price) || price <= 0 || !sourceAccountId) {
        this.showToast("Completa todos los campos obligatorios", "warning");
        return;
      }

      const totalMinor = BigInt(Math.round(qty * price * 100));
      const rawEntries = this.state.entries.map((e) => e.entry);
      const bal = accountBalance(sourceAccountId, rawEntries, optCur, 2);
      if (bal.amountMinor < totalMinor) {
        this.showToast("Saldo insuficiente en la cuenta de origen", "error");
        return;
      }

      // Asegurar cuenta de activo de inversión para el instrumento
      let invAccount = this.state.accounts.find(
        (a) => a.type === "asset" && a.instrumentId === symbol && !a.archived
      );
      if (!invAccount) {
        const accountId = `acc-inv-${symbol.toLowerCase().replace(/[^a-z0-9]/g, "")}-${generateEntryId().slice(0, 4)}`;
        invAccount = {
          accountId,
          name: optName,
          type: "asset",
          currency: optCur,
          parentId: null,
          archived: false,
          instrumentId: symbol,
        };
        await this.localStore.putBatch("accounts", [{ id: accountId, value: invAccount }]);
        this.state.accounts.push(invAccount);
      }

      // Crear asiento equilibrado (INV-1, INV-2, INV-4):
      // Debe inversión (+totalMinor con quantityMinor)
      // Haber liquidez (-totalMinor sin quantityMinor)
      const today = new Date().toISOString().slice(0, 10);
      const entryId = generateEntryId();
      const entry: Entry = {
        entryId,
        date: today,
        description: `Compra ${qty} uds. ${symbol} @ ${price.toFixed(2)} ${optCur}`,
        origin: "manual",
        reversesEntryId: null,
        postings: [
          {
            index: 0,
            accountId: invAccount.accountId,
            amountMinor: totalMinor,
            currency: optCur,
            scale: 2,
            instrumentId: symbol,
            quantityMinor: BigInt(qty),
          },
          {
            index: 1,
            accountId: sourceAccountId,
            amountMinor: -totalMinor,
            currency: optCur,
            scale: 2,
            instrumentId: null,
            quantityMinor: null,
          },
        ],
      };

      const { chained, nextState } = await appendChainedEntry(
        entry,
        this.deviceChainState,
        new Date().toISOString()
      );
      await this.localStore.putBatch("entries", [{ id: chained.entry.entryId, value: chained }]);
      this.deviceChainState = nextState;
      await this.localStore.writeMeta("device_chain_state", nextState);
      this.state.entries.push(chained);

      // Actualizar cotización manual con la operación
      const quote: PriceQuote = {
        instrumentId: symbol,
        asOf: today,
        priceMinor: BigInt(Math.round(price * 100)),
        currency: optCur,
        scale: 2,
        source: "Operación de Compra",
        retrievedAt: new Date().toISOString(),
        manual: true,
      };
      this.quoteProvider.setManualQuote(quote);
      await this.quoteCache.setManualQuote(quote);
      const existingQuoteIdx = this.quotes.findIndex((q) => q.instrumentId === symbol);
      if (existingQuoteIdx >= 0) this.quotes[existingQuoteIdx] = quote;
      else this.quotes.push(quote);

      w.__finvoz_closeBuyModal();
      this.showToast(`Compra registrada: ${qty} uds. de ${symbol}`, "success");
      this.render();
    };

    w.__finvoz_openSellModal = (instrumentId?: string, accountId?: string) => {
      const modal = document.getElementById("sell-instrument-modal");
      if (!modal) return;

      const sel = document.getElementById("sell-instrument-select") as HTMLSelectElement | null;
      if (sel) {
        if (accountId) {
          sel.value = accountId;
        } else if (instrumentId) {
          for (let i = 0; i < sel.options.length; i++) {
            const opt = sel.options[i];
            if (opt && opt.getAttribute("data-instrument-id") === instrumentId) {
              sel.selectedIndex = i;
              break;
            }
          }
        }
      }

      const qtyInput = document.getElementById("sell-quantity") as HTMLInputElement | null;
      if (qtyInput) qtyInput.value = "1";

      modal.style.display = "flex";
      w.__finvoz_onSellInstrumentChange?.();
      w.__finvoz_updateSellSummary?.();
    };

    w.__finvoz_closeSellModal = () => {
      const modal = document.getElementById("sell-instrument-modal");
      if (modal) modal.style.display = "none";
    };

    w.__finvoz_onSellInstrumentChange = () => {
      const sel = document.getElementById("sell-instrument-select") as HTMLSelectElement | null;
      const hintEl = document.getElementById("sell-max-qty-hint");
      const priceInput = document.getElementById("sell-price") as HTMLInputElement | null;
      if (!sel) return;

      const opt = sel.options[sel.selectedIndex];
      if (opt) {
        const qty = opt.getAttribute("data-qty") || "0";
        const symbol = opt.getAttribute("data-instrument-id") || "";
        if (hintEl) hintEl.textContent = `Disponible: ${qty} uds.`;

        const existingQuote = this.quotes.find((q) => q.instrumentId === symbol);
        if (existingQuote && priceInput && !priceInput.value) {
          priceInput.value = (Number(existingQuote.priceMinor) / 100).toFixed(2);
        }
      }
      w.__finvoz_updateSellSummary?.();
    };

    w.__finvoz_updateSellSummary = () => {
      const sel = document.getElementById("sell-instrument-select") as HTMLSelectElement | null;
      const qtyInput = document.getElementById("sell-quantity") as HTMLInputElement | null;
      const priceInput = document.getElementById("sell-price") as HTMLInputElement | null;
      const totalEl = document.getElementById("sell-total-amount");
      const feedbackEl = document.getElementById("sell-qty-feedback");
      const confirmBtn = document.getElementById("btn-confirm-sell") as HTMLButtonElement | null;

      const opt = sel?.options[sel.selectedIndex];
      const maxQty = parseInt(opt?.getAttribute("data-qty") || "0", 10);
      const currency = opt?.getAttribute("data-currency") || this.state.currency;
      const qty = parseInt(qtyInput?.value || "0", 10);
      const price = parseFloat(priceInput?.value || "0");

      const total = qty * price;
      const totalMinor = BigInt(Math.round(total * 100));

      if (totalEl) {
        totalEl.textContent = formatMinorUnits(totalMinor, currency, 2);
      }

      if (isNaN(qty) || qty <= 0 || isNaN(price) || price <= 0) {
        if (feedbackEl) feedbackEl.innerHTML = `<span style="color: var(--warning);">Introduce cantidad y precio válidos.</span>`;
        if (confirmBtn) confirmBtn.disabled = true;
        return;
      }

      if (qty > maxQty) {
        if (feedbackEl) {
          feedbackEl.innerHTML = `<span style="color: var(--danger); font-weight: 600;">⚠️ No puedes vender más de ${maxQty} unidades disponibles.</span>`;
        }
        if (confirmBtn) confirmBtn.disabled = true;
      } else {
        if (feedbackEl) {
          feedbackEl.innerHTML = `<span style="color: var(--success); font-weight: 600;">✓ Cantidad válida (${maxQty - qty} uds. restantes tras venta)</span>`;
        }
        if (confirmBtn) confirmBtn.disabled = false;
      }
    };

    w.__finvoz_submitSellInstrument = async () => {
      const sel = document.getElementById("sell-instrument-select") as HTMLSelectElement | null;
      const qtyInput = document.getElementById("sell-quantity") as HTMLInputElement | null;
      const priceInput = document.getElementById("sell-price") as HTMLInputElement | null;
      const destSel = document.getElementById("sell-dest-account") as HTMLSelectElement | null;

      if (!sel || !qtyInput || !priceInput || !destSel) return;

      const invAccountId = sel.value;
      const opt = sel.options[sel.selectedIndex];
      const symbol = opt?.getAttribute("data-instrument-id") || "";
      const maxQty = parseInt(opt?.getAttribute("data-qty") || "0", 10);
      const currency = opt?.getAttribute("data-currency") || this.state.currency;
      const destAccountId = destSel.value;
      const qty = parseInt(qtyInput.value, 10);
      const price = parseFloat(priceInput.value);

      if (isNaN(qty) || qty <= 0 || isNaN(price) || price <= 0 || !destAccountId || !invAccountId) {
        this.showToast("Completa todos los campos obligatorios", "warning");
        return;
      }

      if (qty > maxQty) {
        this.showToast(`No puedes vender más de las ${maxQty} unidades disponibles`, "error");
        return;
      }

      const totalMinor = BigInt(Math.round(qty * price * 100));

      // Asiento contable de venta (INV-1, INV-2, INV-4):
      // Debe destino liquidez (+totalMinor sin quantityMinor)
      // Haber inversión (-totalMinor, con quantityMinor negativo -BigInt(qty))
      const today = new Date().toISOString().slice(0, 10);
      const entryId = generateEntryId();
      const entry: Entry = {
        entryId,
        date: today,
        description: `Venta ${qty} uds. ${symbol} @ ${price.toFixed(2)} ${currency}`,
        origin: "manual",
        reversesEntryId: null,
        postings: [
          {
            index: 0,
            accountId: destAccountId,
            amountMinor: totalMinor,
            currency,
            scale: 2,
            instrumentId: null,
            quantityMinor: null,
          },
          {
            index: 1,
            accountId: invAccountId,
            amountMinor: -totalMinor,
            currency,
            scale: 2,
            instrumentId: symbol,
            quantityMinor: -BigInt(qty),
          },
        ],
      };

      const { chained, nextState } = await appendChainedEntry(
        entry,
        this.deviceChainState,
        new Date().toISOString()
      );
      await this.localStore.putBatch("entries", [{ id: chained.entry.entryId, value: chained }]);
      this.deviceChainState = nextState;
      await this.localStore.writeMeta("device_chain_state", nextState);
      this.state.entries.push(chained);

      // Actualizar última cotización
      const quote: PriceQuote = {
        instrumentId: symbol,
        asOf: today,
        priceMinor: BigInt(Math.round(price * 100)),
        currency,
        scale: 2,
        source: "Operación de Venta",
        retrievedAt: new Date().toISOString(),
        manual: true,
      };
      this.quoteProvider.setManualQuote(quote);
      await this.quoteCache.setManualQuote(quote);
      const existingQuoteIdx = this.quotes.findIndex((q) => q.instrumentId === symbol);
      if (existingQuoteIdx >= 0) this.quotes[existingQuoteIdx] = quote;
      else this.quotes.push(quote);

      w.__finvoz_closeSellModal();
      this.showToast(`Venta registrada: ${qty} uds. de ${symbol}`, "success");
      this.render();
    };

    w.__finvoz_archiveInstrumentAccount = async (accountId: string) => {
      const acc = this.state.accounts.find((a) => a.accountId === accountId);
      if (!acc) return;
      const confirmed = window.confirm(
        `¿Archivar el instrumento "${acc.name}" (${acc.instrumentId})? FinVoz conserva el histórico contable intacto (INV-3, FR-ACCT-5).`
      );
      if (!confirmed) return;

      const updated = archiveAccount(acc);
      await this.localStore.putBatch("accounts", [{ id: accountId, value: updated }]);
      const idx = this.state.accounts.findIndex((a) => a.accountId === accountId);
      if (idx >= 0) this.state.accounts[idx] = updated;

      this.showToast(`Instrumento "${acc.name}" archivado`, "info");
      this.render();
    };

    w.__finvoz_refreshQuotes = async () => {
      this.showToast("Consultando cotizaciones públicas...", "info");
      const investmentAccounts = this.state.accounts.filter((a) => a.instrumentId && !a.archived);
      const today = new Date().toISOString().slice(0, 10);

      for (const acc of investmentAccounts) {
        const quote = await this.quoteCache.getOrFetch({ instrumentId: acc.instrumentId!, asOf: today });
        if (quote) {
          const existingIdx = this.quotes.findIndex((q) => q.instrumentId === quote.instrumentId);
          if (existingIdx >= 0) this.quotes[existingIdx] = quote;
          else this.quotes.push(quote);
        }
      }
      this.showToast("Cotizaciones actualizadas", "success");
      this.render();
    };

    w.__finvoz_openManualQuoteModal = () => {
      const m = document.getElementById("manual-quote-modal");
      if (m) m.style.display = "flex";
    };
    w.__finvoz_closeManualQuoteModal = () => {
      const m = document.getElementById("manual-quote-modal");
      if (m) m.style.display = "none";
    };

    w.__finvoz_submitManualQuote = async () => {
      const instrumentId = (document.getElementById("mq-instrument") as HTMLInputElement)?.value?.trim().toUpperCase();
      const priceStr = (document.getElementById("mq-price") as HTMLInputElement)?.value;
      const asOf = (document.getElementById("mq-date") as HTMLInputElement)?.value;

      const num = parseFloat(priceStr);
      if (!instrumentId || isNaN(num) || num <= 0 || !asOf) return;

      const quote: PriceQuote = {
        instrumentId,
        asOf,
        priceMinor: BigInt(Math.round(num * 100)),
        currency: this.state.currency,
        scale: 2,
        source: "Manual (Usuario)",
        retrievedAt: new Date().toISOString(),
        manual: true,
      };

      this.quoteProvider.setManualQuote(quote);
      await this.quoteCache.setManualQuote(quote);

      const existingIdx = this.quotes.findIndex((q) => q.instrumentId === quote.instrumentId);
      if (existingIdx >= 0) this.quotes[existingIdx] = quote;
      else this.quotes.push(quote);

      this.showToast(`Precio manual guardado para ${instrumentId}`, "success");
      w.__finvoz_closeManualQuoteModal();
      this.render();
    };

    // --- SIMULADORES ---
    w.__finvoz_switchSimTab = (tab: "ci" | "loan" | "goal") => {
      const pCi = document.getElementById("sim-panel-ci");
      const pLoan = document.getElementById("sim-panel-loan");
      const pGoal = document.getElementById("sim-panel-goal");
      const bCi = document.getElementById("sim-tab-btn-ci");
      const bLoan = document.getElementById("sim-tab-btn-loan");
      const bGoal = document.getElementById("sim-tab-btn-goal");

      if (pCi) pCi.style.display = tab === "ci" ? "block" : "none";
      if (pLoan) pLoan.style.display = tab === "loan" ? "block" : "none";
      if (pGoal) pGoal.style.display = tab === "goal" ? "block" : "none";

      bCi?.classList.replace(tab === "ci" ? "btn-secondary" : "btn-primary", tab === "ci" ? "btn-primary" : "btn-secondary");
      bLoan?.classList.replace(tab === "loan" ? "btn-secondary" : "btn-primary", tab === "loan" ? "btn-primary" : "btn-secondary");
      bGoal?.classList.replace(tab === "goal" ? "btn-secondary" : "btn-primary", tab === "goal" ? "btn-primary" : "btn-secondary");
    };

    w.__finvoz_recalcCompound = () => {
      const principal = BigInt(Math.round((parseFloat((document.getElementById("ci-principal") as HTMLInputElement)?.value) || 0) * 100));
      const monthly = BigInt(Math.round((parseFloat((document.getElementById("ci-contribution") as HTMLInputElement)?.value) || 0) * 100));
      const ratePct = parseFloat((document.getElementById("ci-rate") as HTMLInputElement)?.value) || 0;
      const years = parseInt((document.getElementById("ci-years") as HTMLInputElement)?.value) || 1;

      // Rate per period (monthly) in micros = (ratePct / 12 / 100) * 1_000_000
      const rateMicros = BigInt(Math.round((ratePct / 12 / 100) * 1_000_000));
      const periods = years * 12;

      const res = simulateCompoundInterest(principal, monthly, rateMicros, periods);

      const elFinal = document.getElementById("ci-res-final");
      const elContributed = document.getElementById("ci-res-contributed");
      const elInterest = document.getElementById("ci-res-interest");

      if (elFinal) elFinal.textContent = formatMinorUnits(res.finalBalance, this.state.currency, 2);
      if (elContributed) elContributed.textContent = formatMinorUnits(principal + res.totalContributed, this.state.currency, 2);
      if (elInterest) elInterest.textContent = formatMinorUnits(res.totalInterest, this.state.currency, 2);
    };

    w.__finvoz_recalcLoan = () => {
      const amount = BigInt(Math.round((parseFloat((document.getElementById("loan-amount") as HTMLInputElement)?.value) || 0) * 100));
      const ratePct = parseFloat((document.getElementById("loan-rate") as HTMLInputElement)?.value) || 0;
      const years = parseInt((document.getElementById("loan-years") as HTMLInputElement)?.value) || 1;

      if (amount <= 0n || years <= 0) return;
      const rateMicros = BigInt(Math.round((ratePct / 12 / 100) * 1_000_000));
      const periods = years * 12;

      const schedule = amortizationSchedule(amount, rateMicros, periods);
      const cuota = schedule[0]?.payment ?? 0n;
      const totalPagado = schedule.reduce((sum, r) => sum + r.payment, 0n);
      const totalIntereses = schedule.reduce((sum, r) => sum + r.interest, 0n);

      const elPayment = document.getElementById("loan-res-payment");
      const elTotal = document.getElementById("loan-res-total");
      const elInterest = document.getElementById("loan-res-interest");

      if (elPayment) elPayment.textContent = `${formatMinorUnits(cuota, this.state.currency, 2)} / mes`;
      if (elTotal) elTotal.textContent = formatMinorUnits(totalPagado, this.state.currency, 2);
      if (elInterest) elInterest.textContent = formatMinorUnits(totalIntereses, this.state.currency, 2);
    };

    w.__finvoz_recalcGoalTime = () => {
      const start = BigInt(Math.round((parseFloat((document.getElementById("goal-start") as HTMLInputElement)?.value) || 0) * 100));
      const target = BigInt(Math.round((parseFloat((document.getElementById("goal-target-val") as HTMLInputElement)?.value) || 0) * 100));
      const monthly = BigInt(Math.round((parseFloat((document.getElementById("goal-monthly") as HTMLInputElement)?.value) || 0) * 100));

      const periods = periodsToSavingsGoal(start, monthly, 0n, target);
      const elTime = document.getElementById("goal-res-time");
      if (elTime) {
        elTime.textContent = periods !== null ? `${periods} meses (~${(periods / 12).toFixed(1)} años)` : "Inalcanzable (aportación insuficiente)";
      }
    };

    // --- AJUSTES & SYNC ---
    w.__finvoz_saveGoogleClientId = (id: string) => {
      this.state.googleClientId = id.trim();
      localStorage.setItem("finvoz_google_client_id", this.state.googleClientId);
    };

    w.__finvoz_saveSpreadsheetId = (id: string) => {
      this.state.spreadsheetId = id.trim() || null;
      if (this.state.spreadsheetId) localStorage.setItem("finvoz_spreadsheet_id", this.state.spreadsheetId);
      else localStorage.removeItem("finvoz_spreadsheet_id");
    };

    w.__finvoz_connectGoogleOAuth = () => {
      const clientId =
        this.state.googleClientId ||
        ((typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID)) ||
        DEFAULT_GOOGLE_CLIENT_ID;
      this.state.googleClientId = clientId;

      const redirectUri = getGoogleOAuthRedirectUri();
      const config = createGoogleOAuthConfig(clientId, redirectUri);
      startGoogleOAuth(config);
    };

    w.__finvoz_createNewSheet = async () => {
      const token = this.tokenStore.get()?.accessToken;
      if (!token) {
        this.showToast("Debes conectar tu cuenta de Google antes de crear la hoja", "warning");
        return;
      }
      try {
        this.showToast("Creando hoja en la carpeta FinVoz de Google Drive...", "info");
        const sheetId = await createFinVozSpreadsheet(token);
        this.state.spreadsheetId = sheetId;
        localStorage.setItem("finvoz_spreadsheet_id", sheetId);
        this.showToast("Hoja creada en la carpeta FinVoz y vinculada con éxito", "success");
        this.render();
      } catch (err) {
        this.showToast((err as Error).message, "error");
      }
    };

    w.__finvoz_linkExistingSheet = () => {
      const val = (document.getElementById("spreadsheet-id-input") as HTMLInputElement)?.value.trim();
      if (!val) {
        this.showToast("Introduce un ID de hoja válido", "warning");
        return;
      }
      this.state.spreadsheetId = val;
      localStorage.setItem("finvoz_spreadsheet_id", val);
      this.showToast("Hoja vinculada", "success");
      this.render();
    };

    // --- EXPORTACIÓN A GOOGLE SHEETS, NO AUTORITATIVA (revisión ADR OQ-1, 2026-09-10) ---
    // IndexedDB local es la fuente de autoridad. Sheets nunca se lee de vuelta hacia el
    // libro (eliminado "reconstruir desde Sheets": reescribiría la autoridad local con
    // una fuente que ya no lo es). La recuperación ante pérdida local es la copia de
    // seguridad portátil (`.finvoz.json`), no una hoja externa.
    w.__finvoz_exportMovimientos = async () => {
      const token = this.tokenStore.get()?.accessToken;
      if (!token || !this.state.spreadsheetId) {
        this.showToast("Se requiere sesión de Google y hoja vinculada para exportar", "warning");
        return;
      }
      try {
        this.showToast("Exportando movimientos a Google Sheets (append-only)...", "info");
        const adapter = new GoogleSheetsAdapter({
          spreadsheetId: this.state.spreadsheetId,
          getAccessToken: async () => token,
        });
        const result = await exportMovimientos(adapter, this.state.entries, { mode: "update-existing" });
        this.showToast(
          `Movimientos exportados: ${result.exported} nuevo(s), ${result.skippedAlreadyPresent} ya presentes`,
          "success",
        );
      } catch (err) {
        this.showToast((err as Error).message, "error");
      }
    };

    w.__finvoz_exportResumen = async () => {
      const token = this.tokenStore.get()?.accessToken;
      if (!token || !this.state.spreadsheetId) {
        this.showToast("Se requiere sesión de Google y hoja vinculada para regenerar el resumen", "warning");
        return;
      }
      try {
        this.showToast("Regenerando Resumen actual...", "info");
        const writer = new GoogleSheetsSummaryWriter({
          spreadsheetId: this.state.spreadsheetId,
          getAccessToken: async () => token,
        });
        const result = await exportResumen(writer, this.state.accounts, this.state.entries);
        this.showToast(`Resumen actual regenerado (${result.rowsWritten} cuenta(s))`, "success");
      } catch (err) {
        this.showToast((err as Error).message, "error");
      }
    };

    // --- PERFILES LOCALES (revisión ADR OQ-1, 2026-09-10) ---
    w.__finvoz_submitNewProfile = async () => {
      const nameInput = document.getElementById("new-profile-name") as HTMLInputElement | null;
      const name = nameInput?.value.trim();
      if (!name) {
        this.showToast("El nombre del perfil no puede estar vacío", "warning");
        return;
      }
      const created = await createProfile(this.profileRegistry, name);
      this.state.profiles = await listProfiles(this.profileRegistry);
      const modal = document.getElementById("new-profile-modal");
      if (modal) modal.style.display = "none";
      if (nameInput) nameInput.value = "";
      this.showToast(`Perfil "${created.name}" creado`, "success");
      this.render();
    };

    w.__finvoz_selectProfile = async (profileId: string) => {
      if (profileId === this.state.activeProfileId) return;
      await selectProfile(this.profileRegistry, profileId);
      this.state.activeProfileId = profileId;
      this.localStore = await createProfileDataStore(profileId);
      this.outbox = new Outbox(this.localStore);
      this.quoteCache = new QuoteCache(this.localStore, this.quoteProvider);
      await this.loadLocalData();
      const savedChainState = await this.localStore.readMeta("device_chain_state");
      if (savedChainState) {
        this.deviceChainState = savedChainState as DeviceChainState;
      } else {
        this.deviceChainState = await initDeviceChain("finvoz-ledger-v1", this.deviceChainState.deviceId);
        await this.localStore.writeMeta("device_chain_state", this.deviceChainState);
      }
      await seedInitialAccountsIfEmpty(this.localStore);
      await this.loadLocalData();
      const activeName = this.state.profiles.find((p) => p.profileId === profileId)?.name ?? profileId;
      this.showToast(`Perfil activo: "${activeName}"`, "info");
      this.render();
    };

    w.__finvoz_deleteProfile = async (profileId: string) => {
      const target = this.state.profiles.find((p) => p.profileId === profileId);
      if (!target) return;
      const confirmed = window.confirm(
        `¿Eliminar el perfil "${target.name}"? Sus datos locales de este dispositivo dejarán de ser accesibles desde FinVoz. Exporta una copia de seguridad antes si quieres conservarlos.`,
      );
      if (!confirmed) return;
      try {
        const result = await deleteProfile(this.profileRegistry, profileId);
        this.state.profiles = await listProfiles(this.profileRegistry);
        if (result.wasActive && result.nextActiveProfileId) {
          await w.__finvoz_selectProfile(result.nextActiveProfileId);
        } else {
          this.render();
        }
        this.showToast(`Perfil "${target.name}" eliminado`, "info");
      } catch (err) {
        this.showToast((err as Error).message, "error");
      }
    };

    // --- COPIA DE SEGURIDAD PORTÁTIL `.finvoz.json` (FR-BACKUP-*, revisión ADR OQ-1) ---
    w.__finvoz_exportBackup = async () => {
      const activeProfile = this.state.profiles.find((p) => p.profileId === this.state.activeProfileId);
      if (!activeProfile) return;
      const file = await exportBackup({
        localStore: this.localStore,
        profile: { profileId: activeProfile.profileId, name: activeProfile.name },
        settings: {
          theme: this.state.theme,
          ...(this.state.googleClientId ? { googleClientId: this.state.googleClientId } : {}),
          spreadsheetId: this.state.spreadsheetId,
        },
      });
      const text = serializeBackupFile(file);
      const blob = new Blob([text], { type: "application/json;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${activeProfile.name.replace(/[^a-zA-Z0-9_-]+/g, "_")}-${new Date().toISOString().slice(0, 10)}.finvoz.json`;
      a.click();
      URL.revokeObjectURL(url);
      this.showToast("Copia de seguridad exportada", "success");
    };

    let pendingBackupFile: FinvozBackupFile | null = null;

    w.__finvoz_onBackupFileSelected = (file: File) => {
      const reader = new FileReader();
      reader.onload = async (re) => {
        const text = re.target?.result as string;
        try {
          const parsed = await parseBackupFile(text);
          pendingBackupFile = parsed;
          const preview: BackupImportPreview = {
            fileName: file.name,
            profileName: parsed.payload.profile.name,
            exportedAt: parsed.payload.exportedAt,
            accountsCount: parsed.payload.accounts.length,
            entriesCount: parsed.payload.entries.length,
            budgetsCount: parsed.payload.budgets.length,
            goalsCount: parsed.payload.goals.length,
            priceQuotesCount: parsed.payload.priceQuotes.length,
            rawText: text,
            error: null,
          };
          this.state.pendingImportPreview = preview;
        } catch (err) {
          pendingBackupFile = null;
          this.state.pendingImportPreview = {
            fileName: file.name,
            profileName: "",
            exportedAt: "",
            accountsCount: 0,
            entriesCount: 0,
            budgetsCount: 0,
            goalsCount: 0,
            priceQuotesCount: 0,
            rawText: text,
            error: (err as Error).message,
          };
        }
        this.render();
      };
      reader.readAsText(file);
    };

    w.__finvoz_confirmImportBackup = async (mode: ImportMode) => {
      if (!pendingBackupFile) {
        this.showToast("Selecciona primero un archivo de copia de seguridad válido", "warning");
        return;
      }
      try {
        let targetStore = this.localStore;
        let targetProfileId = this.state.activeProfileId!;

        if (mode === "new-profile") {
          const created = await createProfile(this.profileRegistry, `${pendingBackupFile.payload.profile.name} (restaurado)`);
          this.state.profiles = await listProfiles(this.profileRegistry);
          targetStore = await createProfileDataStore(created.profileId);
          targetProfileId = created.profileId;
        }

        const result = await importBackup({ file: pendingBackupFile, mode, targetLocalStore: targetStore });

        if (mode === "new-profile") {
          await w.__finvoz_selectProfile(targetProfileId);
        } else {
          await this.loadLocalData();
        }

        this.state.importBackupModalOpen = false;
        this.state.pendingImportPreview = null;
        pendingBackupFile = null;

        this.showToast(
          `Importación (${mode}): ${result.entriesWritten} asiento(s) nuevo(s)` +
            (result.entriesSkippedAsDuplicate > 0 ? `, ${result.entriesSkippedAsDuplicate} duplicado(s) omitido(s)` : ""),
          "success",
        );
        this.render();
      } catch (err) {
        this.showToast((err as Error).message, "error");
      }
    };

    w.__finvoz_promptPwaInstall = async () => {
      if (this.deferredPrompt) {
        this.deferredPrompt.prompt();
        const choice = await this.deferredPrompt.userChoice;
        if (choice.outcome === "accepted") {
          this.showToast("Instalación de PWA iniciada", "success");
        }
        this.deferredPrompt = null;
      } else {
        this.showToast("Para instalar en iOS/Safari usa 'Compartir' -> 'Añadir a pantalla de inicio'. En Chrome, usa el icono de instalación en la barra.", "info");
      }
    };

    w.__finvoz_confirmPurgeLocal = async () => {
      const confirmed = window.confirm(
        "¿Estás seguro de purgar todos los datos locales? Se borrarán cuentas, asientos, cotizaciones y outbox de este dispositivo. Tu hoja de Google Sheets NO se modificará (FR-STORE-8)."
      );
      if (!confirmed) return;

      await logoutAndPurgeLocalData(this.tokenStore, this.localStore);
      localStorage.removeItem("finvoz_spreadsheet_id");
      this.state.entries = [];
      this.state.outboxItems = [];
      this.state.budgets = [];
      this.state.goals = [];
      this.state.googleUserEmail = null;
      this.state.spreadsheetId = null;

      // Re-sembrar cuentas iniciales
      await seedInitialAccountsIfEmpty(this.localStore);
      await this.loadLocalData();

      this.showToast("Datos locales purgados por completo", "info");
      this.render();
    };

    w.__finvoz_exportVoiceDiagnostics = () => {
      const logs = typeof localStorage !== "undefined" ? localStorage.getItem("finvoz_unrecognized_voice_logs") || "[]" : "[]";
      const blob = new Blob([logs], { type: "application/json;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `finvoz-voice-dataset-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      this.showToast("Dataset de diagnóstico de voz descargado", "success");
    };

    w.__finvoz_clearVoiceDiagnostics = () => {
      if (typeof localStorage !== "undefined") {
        localStorage.removeItem("finvoz_unrecognized_voice_logs");
      }
      this.showToast("Historial de diagnóstico de voz borrado", "info");
      this.render();
    };
  }

  private async confirmNewEntry(params: {
    date: string;
    description: string;
    origin: "voice" | "manual" | "import";
    debitAccountId: string;
    creditAccountId: string;
    amountMinor: bigint;
    currency: string;
    scale: number;
  }): Promise<void> {
    const entryId = generateEntryId();
    const postings = [
      {
        index: 0,
        accountId: params.debitAccountId,
        amountMinor: params.amountMinor, // Debe (+)
        currency: params.currency,
        scale: params.scale,
        quantityMinor: null,
        instrumentId: null,
      },
      {
        index: 1,
        accountId: params.creditAccountId,
        amountMinor: -params.amountMinor, // Haber (-)
        currency: params.currency,
        scale: params.scale,
        quantityMinor: null,
        instrumentId: null,
      },
    ];

    const entry = buildEntry({
      entryId,
      date: params.date,
      description: params.description,
      origin: params.origin,
      postings,
    });

    // F2: transaccion local atomica
    const { chained, nextState } = await appendChainedEntry(
      entry,
      this.deviceChainState,
      new Date().toISOString()
    );

    await this.localStore.putBatch("entries", [{ id: chained.entry.entryId, value: chained }]);
    // Sin encolar en Outbox: ver comentario equivalente en __finvoz_promptReverseEntry.
    this.deviceChainState = nextState;
    await this.localStore.writeMeta("device_chain_state", nextState);

    this.state.entries.push(chained);

    this.showToast(`Asiento "${params.description}" confirmado (#${chained.deviceSeq})`, "success");
    this.render();

    // Sin exportación automática (revisión de ADR OQ-1, 2026-09-10): el asiento queda
    // confirmado y autoritativo en IndexedDB local. Exportar a Sheets es una acción
    // manual y explícita del usuario, nunca disparada por confirmar un asiento.
  }

  // `syncOutbox` (motor de sincronizacion automatica contra Sheets-como-autoridad)
  // se retiro de aqui tras la auditoria de Codex (reviews/CODEX.md, "Auditoria del
  // cambio local-first", punto 3): era codigo muerto, sin ningun llamador, y una
  // superficie de regresion hacia el modelo anterior. `src/sync/sync-engine.ts` y
  // `src/storage/outbox.ts` NO se eliminan: quedan como infraestructura probada y
  // diferida para una futura fase de sincronizacion automatica multi-dispositivo
  // (empaquetado APK), documentada en `decisions/ADR-OQ-1-sheets-sync-authority.md`
  // "Revision 2026-09-10". La unica ruta activa hoy es la exportacion manual
  // (`__finvoz_exportMovimientos` / `__finvoz_exportResumen`, arriba).
}
